import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Content from "../..//layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
  Tag,
  Modal,
  Descriptions,
  Form,
  Input,
  DatePicker,
  Upload,
  Tabs,
  Select,
  Radio,
  Tooltip,
  Progress,
  Checkbox,
  Row as AntRow,
  Col as AntCol,
  Button as AntdButton,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  RightOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileTextOutlined,
  DollarOutlined,
  InboxOutlined,
  SendOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  FilePdfOutlined,
  CheckOutlined,
  CloseOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  ScheduleOutlined,
  PauseCircleOutlined,
  UnlockOutlined,
  WarningOutlined,
  PlusCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  PendingWorkOrderData,
  WorkOrderData,
  WorkOrderLine,
  WorkOrderLineSource,
  PaymentCondition,
} from "./types";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Swal from "sweetalert2";
import { formatDecimal } from "../../utils/Utils";
import { message } from "antd";
import workExecutionApis from "../../api/WorkOrderManagement/WorkExecutionApis";
import WorkOrderApprovalDrawer from "./workOrderCreation/WorkOrderApprovalDrawer";
import { ChangeRequestStatus } from "./mockData/workOrderAmendmentData";
import WorkOrderPdfGenerator from "./WorkOrderPdfGenerator";
import SignatureModal from "../components/SignatureModal";
import ImageUploadCropper from "../components/ImageUploadCropper";
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Dragger } = Upload;

dayjs.extend(relativeTime);

const { Panel } = AntCollapse;

// Helper function to check if payment conditions is in array format
const isPaymentConditionsArray = (
  paymentConditions: any
): paymentConditions is PaymentCondition[] => {
  return Array.isArray(paymentConditions);
};

const WorkOrderView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";

  const [workOrder, setWorkOrder] = useState<WorkOrderData | null>(null);
  const [amendmentData, setAmendmentData] = useState<any | null>(null);
  const [pendingApprovalsData, setPendingApprovals] =
    useState<PendingWorkOrderData | null>(null);
  const [isAmendmentView, setIsAmendmentView] = useState(false);
  const [isPendingApprovalsWorkOrderView, setIsPendingApprovalsWorkOrderView] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [disableApprove, setDisableApprove] = useState(false);
  const [disableReject, setDisableReject] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const [executionModalVisible, setExecutionModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [workLines, setWorkLines] = useState<any[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState<any>({
    isActive: false,
    label: "Authorised Signatory",
    image: null,
  });
  const [activeSignatureContext, setActiveSignatureContext] = useState<
    'APPROVE' | 'REJECT' | null
  >(null);
  const [pendingRejectReason, setPendingRejectReason] = useState<string>("");
  const [isStrictMode, setIsStrictMode] = useState<boolean>(false);
  const [approverIds, setApproverIds] = useState<any>([]);

  const [isModalApproveVisible, setIsModalApproveVisible] = useState(false);
  const [isModalRejectedVisible, setIsModalRejectedVisible] = useState(false);

  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const [currentUserId, setCurrentUserId] = useState<any>(null);

  // Mock current user permissions
  const [allowedActions] = useState({
    view: 1,
    edit: 1,
    approve: 1,
    reject: 1,
  });

  useEffect(() => {
    if (id) {
      loadWorkOrderData(id);
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  useEffect(() => {
    if (currentUser) {
      const { id } = currentUser;
      setCurrentUserId(id);
    }
  }, [currentUser]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const uploadProps = {
    multiple: true,
    fileList,
    onChange: ({ fileList: newFileList }: any) => setFileList(newFileList),
    beforeUpload: () => false, // Prevent auto upload
    accept: ".pdf,.jpg,.jpeg,.png,.docx",
  };

  const loadWorkOrderData = async (workOrderId: string) => {
    try {
      setLoading(true);

      if (!workOrderId) {
        message.error("Work Order ID is required");
        setLoading(false);
        return;
      }

      console.log("Fetching Work Order with ID:", workOrderId);

      const response: any = await workOrderApis.getWorkOrderById(workOrderId);

      if (response && response.status && response.data) {
        const apiData = response.data;
        console.log("API Response:", apiData);

        const isStrict =
          response?.data?.isStrictApproval === true ||
          response?.data?.isStrictApproval === 1;
        setIsStrictMode(isStrict);

        if (response.data?.workOrderApprovals) {
          const processedApprovals = response.data.workOrderApprovals.map((approval: any) => ({
            ...approval,
            level: approval.level?.toLowerCase() || approval.level,
            status: approval.status?.toLowerCase() || approval.status
          }));
          response.data.workOrderApprovals = processedApprovals;

          let ids = processedApprovals
            ?.filter((item: any) => item?.status?.toLowerCase() !== "approved")
            ?.map((item: any) => item?.userId);
          setApproverIds(ids);
        }

        // Map work order lines with proper structure
        const mappedWorkOrderLines =
          apiData.workOrderLines?.map((line: any) => ({
            id: line.workOrderLineId?.toString() || "",
            taskTitle: line.taskTitle || "",
            description: line.description || "",
            workCategory: line.workCategoryId?.toString() || "",
            workCategoryName: line.workCategoryName || "",
            quantity: line.quantity || 0,
            uom: line.uomId?.toString() || "",
            uomName: line.uomName || "",
            allocatedQty: line.allocatedQty || 0,
            weightagePct: line.weightagePct || 0,
            rate: line.rate || 0,
            remarks: line.remarks || "", // Rate change remarks
            totalExecutedQty: line.totalExecutedQty || 0,
            totalApprovalExecutedQty: line.totalApprovalExecutedQty || 0,
            totalBilledQty: line.totalBilledQty || 0,
            availableQtyToHold: line.availableQtyToHold || 0,
            totalOnHoldQty: line.totalOnHoldQty || 0,
            executionStatus: line.executionStatus || "Not Started",
            taxType: line.taxId?.toString() || "",
            taxTypeName: line.taxName || "",
            taxPercentage: parseFloat(
              line?.taxName?.split(" - ")[1]?.replace("%", "") || "0"
            ),
            taxAmount: line.taxAmount || 0,
            sgstRate: line.sgstRate || 0,
            cgstRate: line.cgstRate || 0,
            igstRate: line.igstRate || 0,
            igstAmount: line.igstAmount,
            cgstAmount: line.cgstAmount,
            sgstAmount: line.sgstAmount,
            lineValue: line.lineValue || 0,
            sequence: line.sequence || 1,
            source: line.sourceType || "New Work Line",
            status: line.status || "Active",
            uploadedDocs: line.dms || [],
            // Budget allocation fields
            budgetLineId: line.budgetLineId || 0,
            budgetLineName: line.budgetLineName || "",
            budgetAllocation: line.budgetAllocation || 0,
            // Work Level and Location fields for New Work Line
            workLevel: apiData.segmentLevel || line.segmentLevel || "",
            workType: apiData.workType,
            towerVilla: line.siteStructureUnitName || "",
            floorName: line.floorNo || "",
            flatName: line.siteStructureSubUnitName || "",
            locationName: line.locationName || "",
            // Stage breakdown for schedule
            stages:
              line.stages?.map((stage: any) => ({
                workOrderLineStageId: stage.workOrderLineStageId,
                stageTitle: stage.stageTitle || "",
                splitPercentage: stage.splitPercentage || 0,
                rate: stage.rate || 0,
                amount: stage.subtotal,
                allocatedQty: stage.allocatedQty || 0,
                totalExecutedQty: stage.totalExecutedQty || 0,
                totalApprovalExecutedQty: stage.totalApprovalExecutedQty || 0,
                totalBilledQty: stage.totalBilledQty || 0,
                totalOnHoldQty: stage.totalOnHoldQty || 0,
              })) || [],
          })) || [];

        // Map general conditions
        const mappedGeneralConditions =
          apiData.workOrderGeneralConditions?.map((condition: any) => ({
            id: condition.workOrderConditionId?.toString() || "",
            conditionCode: condition.conditionCode || "",
            content: condition.content || "",
            createdAt: condition.createdAt || "",
            updatedAt: condition.updatedAt || "",
          })) || [];

        // Map payment conditions
        const mappedPaymentConditions =
          apiData.workOrderPaymentConditions?.map((condition: any) => ({
            id: condition.workOrderPaymentTermId?.toString() || "",
            conditionType: condition.conditionType || "",
            isPercent: condition.isPercent || false,
            percentValue: condition.percentValue || 0,
            amountValue: condition.amountValue || 0,
            interestCharged: condition?.interestCharged || false,
            interestRate: condition.interestRate || 0,
            recoveryType: condition.recoveryType || "",
            releaseDate: condition.releaseDate || "",
            recoveryPercentRate: condition.recoveryPercentRate || null,
            recoveryCapPercent: condition.recoveryCapPercent || null,
            notes: condition.notes || "",
            calculatedAmount: condition.calculatedAmount || 0,
            createdAt: condition.createdAt || "",
            updatedAt: condition.updatedAt || "",
          })) || [];

        // Map approvals
        const mappedApprovals =
          apiData.workOrderApprovals?.map((approval: any) => ({
            id: approval.workOrderApprovalId?.toString() || "",
            userId: approval.userId || "",
            approverName: approval.approverName || "",
            approverRole: approval.approverRole || "",
            level: approval.level || "",
            status: approval.status || "",
            approvalStage: approval.approvalStage || "",
            approvedByName: approval.approvedByName || "",
            reason: approval.reason || "",
            createdAt: approval.createdAt || "",
          })) || [];

        // Map API response to WorkOrderData interface
        const mappedWorkOrder: any = {
          id: apiData.workOrderId?.toString() || workOrderId,
          workOrderNumber: apiData.workOrderNumber || "",
          workOrderTitle: apiData.workOrderTitle || "",
          description: apiData.description || "",
          contractDate: apiData.contractDate || "",
          siteId: apiData.siteId?.toString() || "",
          siteName: apiData.siteName || "",
          projectName: apiData.projectName || "",
          contractType: apiData.contractType || "",
          contractWith: apiData.contractWith || "",
          contractorId: apiData.contractorId?.toString() || "",
          contractorName: apiData.contractorName || "",
          workOrderOwnerId: apiData.workOrderOwnerId?.toString() || "",
          workOrderOwnerName: apiData.workOrderOwnerName || "",
          plannedStartDate: apiData.plannedStartDate || "",
          plannedEndDate: apiData.plannedEndDate || "",
          approvalRequired: apiData.approvalRequired || false,
          enableTax: apiData.enableTax || false,
          taxApplicationLevel: apiData.taxLevel || "",
          headerTaxId: apiData.headerTaxId,
          headerTaxName: apiData.headerTaxName || "",
          tdsTaxId: apiData.tdsTaxId,
          tdsTaxName: apiData.tdsTaxName || "",
          tdsRate: apiData.tdsRate || 0,
          tcsTaxId: apiData.tcsTaxId,
          tcsTaxName: apiData.tcsTaxName || "",
          tcsRate: apiData.tcsRate || 0,
          budgetId: apiData.budgetId,
          budgetName: apiData.budgetName || "",
          sourceType: apiData.sourceType || "",
          segmentLevel: apiData.segmentLevel || "",
          workType: apiData.workType || "",
          executionStatus: apiData.executionStatus,
          progressPercent: apiData.progressPercent || 0,
          status: apiData.status || "Draft",
          totalValue: apiData.totalValue || 0,
          taxAmount: apiData.taxAmount || 0,
          grossValue: apiData.grossValue || 0,
          outstandingAmount: apiData.outstandingAmount || 0,
          createdBy: apiData.createdByName || "Unknown",
          createdAt: apiData.createdAt || new Date().toISOString(),
          updatedAt: apiData.updatedAt || apiData.createdAt,
          updatedBy: apiData.updatedByName || "",
          orgName: apiData.orgName || "",
          orgId: apiData.orgId?.toString() || "",
          // Budget information
          budgetHeaderId: apiData.budgetHeaderId || 0,
          // budgetName: apiData.budgetName || "",
          // Mapped arrays
          workOrderLines: mappedWorkOrderLines,
          generalConditions: mappedGeneralConditions,
          paymentConditions: mappedPaymentConditions,
          approvals: mappedApprovals,
          attachments: apiData.attachments || [],
          dms: apiData.dms || [],
          contractorChangeRequests: apiData.contractorChangeRequests || [],
          riskflassItems:
            apiData.workOrderAdvanceDeductions?.filter(
              (item: any) => item.type === "Risk Flag"
            ) || [],
          advanceItems:
            apiData.workOrderAdvanceDeductions?.filter(
              (item: any) =>
                item.type === "Advance" || item.type === "Mobilization Advance"
            ) || [],
          workOrderApprovals: mappedApprovals,
        };

        // Check if this work order is in amendment mode (Changes Requested status)
        const isAmendment = mappedWorkOrder.status === "Changes Requested";

        //  const isAmendment =
        // mappedWorkOrder.status === "Changes Requested" ||
        // (mappedWorkOrder.contractorChangeRequests &&
        //   mappedWorkOrder.contractorChangeRequests.length > 0);

        // Map contractor change requests to amendment data format
        const mappedAmendmentData =
          mappedWorkOrder.contractorChangeRequests &&
            mappedWorkOrder.contractorChangeRequests.length > 0
            ? {
              changeRequestStatus:
                mappedWorkOrder.status === "Changes Requested"
                  ? "Changes Requested"
                  : "Change Requested",
              changeRequestedBy:
                mappedWorkOrder.contractorChangeRequests[0]?.createdByName ||
                "Unknown",
              changeRequestDate:
                mappedWorkOrder.contractorChangeRequests[0]?.createdAt ||
                new Date().toISOString(),
              changeRequestComments:
                mappedWorkOrder.contractorChangeRequests.map(
                  (request: any) => ({
                    commentBy: request.createdByName || "Unknown",
                    commentRole: "Contractor",
                    commentDate:
                      request.createdAt || new Date().toISOString(),
                    commentText: request.changeReason || "No reason provided",
                    changeType: "Amendment Request",
                  })
                ),
              changeRequestFiles:
                mappedWorkOrder.contractorChangeRequests.reduce(
                  (files: any[], request: any) => {
                    if (request.dms && Array.isArray(request.dms)) {
                      const requestFiles = request.dms.map((file: any) => ({
                        fileName: file.fileName || "Unknown File",
                        fileType: file.fileType || "application/octet-stream",
                        fileSize: file.fileSize || 0,
                        category: file.moduleFileType || "General",
                        uploadedBy: request.createdByName || "Unknown",
                        uploadedDate:
                          request.createdAt || new Date().toISOString(),
                        description: `Change request attachment`,
                        filePath: file.filePath || "",
                        docId: file.docId,
                      }));
                      return [...files, ...requestFiles];
                    }
                    return files;
                  },
                  []
                ),
            }
            : null;

        setWorkOrder(mappedWorkOrder);
        setIsAmendmentView(isAmendment);
        setAmendmentData(mappedAmendmentData);

        // Initialize work lines for execution modal
        const initialWorkLines = mappedWorkOrderLines.map((line: any) => ({
          id: line.id,
          taskTitle: line.taskTitle,
          workCategoryName: line.workCategoryName,
          uomName: line.uomName,
          approvedQuantity: line.quantity,
          allocatedQuantity: line.allocatedQty || 0,
          remainingQuantity: line.quantity - (line.allocatedQty || 0),
          executedQuantity: 0,
          remarks: "",
          rate: line.rate,
        }));
        setWorkLines(initialWorkLines);

        message.success("Work Order details loaded successfully");
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work Order details"
        );
      }
      // }
    } catch (error: any) {
      console.error("Error loading work order:", error);
      message.error("Failed to load work order data");
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.message || "Failed to load work order data. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const parseLevelNum = (level: any) => {
    if (!level && level !== 0) return NaN;
    const str = String(level).toLowerCase();

    // Handle various level formats: level1, Level2, L1, L2, etc.
    if (str.includes('level')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    } else if (str.startsWith('l')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    }
    return NaN;
  };

  const hasUserAlreadyApproved = workOrder?.workOrderApprovals?.some(
    (a: any) =>
      a.userId === currentUser?.id &&
      a.status?.toUpperCase() === "APPROVED"
  );

  const canUserApproveReject = useCallback(() => {
    // 1. Admin can Approve/Reject anytime - Buttons are always ENABLED
    if (currentUser?.genericRole?.toLowerCase() === "admin") {
      return true;
    }

    const approvals = workOrder?.workOrderApprovals || [];

    // Find current user's approval entry
    const userApproval = approvals.find(
      (approval: any) => approval.userId === currentUserId
    );

    if (!userApproval || userApproval.status?.toLowerCase() !== "pending") {
      return false;
    }

    const userLevelNum = parseLevelNum(userApproval.level ?? currentUser?.approvalLevel);

    // 2. Strict = 0 (Flexible mode)
    // Anyone in the approval list can approve at any time
    if (isStrictMode === false) {
      // In flexible mode, just check if user is in the approval list and pending
      return true;
    }

    // 3. Strict = 1 (Sequential mode)
    // Must follow the order. Level 2 cannot see buttons until Level 1 is "Approved"
    if (isStrictMode === true) {
      // Get all pending approvals sorted by level
      const pendingApprovals = approvals
        .filter((approval: any) => approval.status?.toLowerCase() === "pending")
        .sort((a: any, b: any) => {
          const aNum = parseLevelNum(a.level);
          const bNum = parseLevelNum(b.level);
          if (isNaN(aNum) || isNaN(bNum)) return 0;
          return aNum - bNum;
        });

      // If there are no pending approvals, user can't approve
      if (pendingApprovals.length === 0) return false;

      // Get the first pending approval (lowest level)
      const firstPending = pendingApprovals[0];
      const firstPendingLevelNum = parseLevelNum(firstPending.level);

      // Check if user is the first pending approver
      return userApproval.userId === firstPending.userId &&
        userLevelNum === firstPendingLevelNum;
    }

    return false;
  }, [currentUser, currentUserId, workOrder?.workOrderApprovals, isStrictMode]);

  const handleEdit = () => {
    if (id) {
      const searchParams = new URLSearchParams(location.search);
      const flowType = searchParams.get("flowType");
      const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
      navigate(`/work-order-creation/?edit=${id}${flowTypeQuery}`);
    }
  };

  const handleDuplicate = () => {
    if (id) {
      const searchParams = new URLSearchParams(location.search);
      const flowType = searchParams.get("flowType");
      const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
      navigate(`/work-order-creation/?duplicate=${id}${flowTypeQuery}`);
    }
  };

  const handleDelete = () => {
    if (!workOrder) return;

    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        // Mock delete functionality
        Swal.fire("Deleted!", "Work Order has been deleted.", "success").then(
          () => {
            const searchParams = new URLSearchParams(location.search);
            const flowType = searchParams.get("flowType");
            const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
            navigate(`/view-work-orders${flowTypeQuery}`);
          }
        );
      }
    });
  };

  const handleApprove = async (signatureData?: string) => {
    try {
      setDisableApprove(true);

      if (!workOrder?.id) {
        message.error("Work Order ID is required");
        return;
      }

      // Build files array if signature is present
      const files: File[] = [];
      if (signatureData) {
        files.push(dataURLToFile(signatureData, "signature.png"));
      }

      const workOrderId = parseInt(workOrder.id);

      console.log("Approving work order:", { workOrderId, files });

      // Call the approve API that accepts files
      const response: any = await workOrderApis.approveWorkOrder(workOrderId, files);

      if (response && response.status) {
        Swal.fire(
          "Approved!",
          response.message || "Work Order has been approved successfully",
          "success"
        ).then(() => {
          const searchParams = new URLSearchParams(location.search);
          const flowType = searchParams.get("flowType");
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(`/work-order-approvals${flowTypeQuery}`);
        });
      } else {
        throw new Error(response?.message || "Failed to approve work order");
      }
    } catch (error: any) {
      console.error("Error approving work order:", error);
      Swal.fire(
        "Error!",
        error.message || "Failed to approve Work Order. Please try again.",
        "error"
      );
    } finally {
      setDisableApprove(false);
    }
  };

  const handleReject = async (rejectedReason: string, signatureData?: string) => {
    try {
      setDisableReject(true);

      if (!workOrder?.id) {
        message.error("Work Order ID is required");
        return;
      }

      // Build files array if signature is present
      const files: File[] = [];
      if (signatureData) {
        files.push(dataURLToFile(signatureData, "signature.png"));
      }

      const workOrderId = parseInt(workOrder.id);

      console.log("Rejecting work order:", { workOrderId, reason: rejectedReason, files });

      // Call the reject API that accepts files
      const response: any = await workOrderApis.rejectWorkOrder(workOrderId, rejectedReason, files);

      if (response && response.status) {
        Swal.fire(
          "Rejected!",
          response.message || "Work Order has been rejected successfully",
          "success"
        ).then(() => {
          const searchParams = new URLSearchParams(location.search);
          const flowType = searchParams.get("flowType");
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(`/work-order-approvals${flowTypeQuery}`);
        });
      } else {
        throw new Error(response?.message || "Failed to reject work order");
      }
    } catch (error: any) {
      console.error("Error rejecting work order:", error);
      Swal.fire(
        "Error!",
        error.message || "Failed to reject Work Order. Please try again.",
        "error"
      );
    } finally {
      setDisableReject(false);
    }
  };

  const showRejectModal = () => {
    // Open combined reject modal that contains reason + signature selection (like Buyer app)
    setPendingRejectReason("");
    form.resetFields(["reason"]);
    setActiveSignatureContext("REJECT");
    setSignature({ isActive: false, label: "Authorised Signatory", image: null });
    setIsModalRejectedVisible(true);
  };

  const dataURLToFile = (dataUrl: string, fileName: string): File => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  const openApproveConfirmSwal = async () => {
    if (!signature.image) {
      Swal.fire({
        icon: "info",
        title: "Signature Required",
        text: "Please add your signature before approving.",
      });
      return;
    }

    setIsModalApproveVisible(false);

    const res = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve it!",
      cancelButtonText: "No, keep it",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (res.isConfirmed) {
      try {
        await handleApprove(signature.image);
      } finally {
        setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        setActiveSignatureContext(null);
        setDisableApprove(false);
      }
    } else {
      setIsModalApproveVisible(true);
      setDisableApprove(false);
    }
  };

  const openRejectConfirmSwal = async () => {
    if (!pendingRejectReason || !pendingRejectReason.trim()) {
      Swal.fire({
        icon: "info",
        title: "Reason Required",
        text: "Please provide a rejection reason before submitting.",
      });
      return;
    }

    if (!signature.image) {
      Swal.fire({
        icon: "info",
        title: "Signature Required",
        text: "Please add your signature before rejecting.",
      });
      return;
    }

    setIsModalRejectedVisible(false);

    const res = await Swal.fire({
      title: "Confirm Rejection?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Reject it!",
      cancelButtonText: "Cancel",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (res.isConfirmed) {
      try {
        await handleReject(pendingRejectReason, signature.image);
      } finally {
        setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        setActiveSignatureContext(null);
        setPendingRejectReason("");
        setDisableReject(false);
      }
    } else {
      setIsModalRejectedVisible(true);
      setDisableReject(false);
    }
  };

  const handleApproveAmendment = async () => {
    try {
      setDisableApprove(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      Swal.fire(
        "Approved!",
        "Work Order amendment has been approved successfully",
        "success"
      ).then(() => {
        const searchParams = new URLSearchParams(location.search);
        const flowType = searchParams.get("flowType");
        const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
        navigate(`/work-order-contractor-approvals${flowTypeQuery}`);
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to approve amendment", "error");
    } finally {
      setDisableApprove(false);
    }
  };

  const handleRejectAmendment = async (rejectedReason: string) => {
    try {
      setDisableReject(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      Swal.fire(
        "Rejected!",
        "Work Order amendment has been rejected",
        "success"
      ).then(() => {
        const searchParams = new URLSearchParams(location.search);
        const flowType = searchParams.get("flowType");
        const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
        navigate(`/work-order-contractor-approvals${flowTypeQuery}`);
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to reject amendment", "error");
    } finally {
      setDisableReject(false);
    }
  };


  const handleReleaseWorkOrder = async () => {
    try {
      if (!workOrder?.id) {
        message.error("Work Order ID is required");
        return;
      }

      // Show confirmation dialog
      const result = await Swal.fire({
        title: "Release Work Order?",
        text: "Are you sure you want to release this work order? This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, Release Work Order",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        // Prepare request body
        const requestBody = {
          workOrderId: workOrder.id,
        };

        console.log("Releasing work order:", requestBody);

        // Call the release API
        const response: any = await workOrderApis.releaseWorkOrder(requestBody);

        if (response && response.status) {
          Swal.fire(
            "Released!",
            "Work Order has been released successfully",
            "success"
          ).then(() => {
            // Optionally navigate back to work orders list
            const searchParams = new URLSearchParams(location.search);
            const flowType = searchParams.get("flowType");
            const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
            navigate(`/view-work-orders${flowTypeQuery}`);
          });
        } else {
          throw new Error(response?.message || "Failed to release work order");
        }
      }
    } catch (error: any) {
      console.error("Error releasing work order:", error);
      Swal.fire(
        "Error!",
        error.message || "Failed to release work order. Please try again.",
        "error"
      );
    }
  };

  const showRejectAmendmentModal = () => {
    Swal.fire({
      title: "Reject Amendment Request",
      html: `
        <textarea 
          id="rejection-reason" 
          placeholder="Enter rejection reason (required)"
          style="width: 100%; height: 100px; margin-top: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
        ></textarea>
      `,
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Reject Amendment",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = (
          document.getElementById("rejection-reason") as HTMLTextAreaElement
        )?.value;
        if (!reason.trim()) {
          Swal.showValidationMessage("Rejection reason is required");
          return false;
        }
        return reason;
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        handleRejectAmendment(result.value);
      }
    });
  };

  const handleSubmitForApproval = async () => {
    try {
      const values = await form.validateFields();

      // Validate that at least one line has executed quantity > 0
      const hasExecutedWork = workLines.some(
        (line) => line.executedQuantity > 0
      );

      if (!hasExecutedWork) {
        message.error(
          "At least one work line must have executed quantity greater than 0 to submit for approval"
        );
        return;
      }

      setSubmitting(true);

      // Prepare line updates for lines with executed quantity > 0
      const lineUpdates = workLines
        .filter((line) => line.executedQuantity > 0)
        .map((line) => ({
          workOrderLineId: line.id,
          executedQuantity: line.executedQuantity,
          remarks: line.remarks || "",
        }));

      // Create files data array if files are uploaded
      const filesData = fileList.map((file, index) => ({
        order: index,
        module: "Work Order Management",
        moduleFileType: "Execution Report",
      }));

      // Prepare execution dates from the form
      const executionPeriod = values.executionPeriod;
      const executionStartDate = executionPeriod?.[0]
        ? executionPeriod[0].format("YYYY-MM-DD")
        : "";
      const executionEndDate = executionPeriod?.[1]
        ? executionPeriod[1].format("YYYY-MM-DD")
        : "";

      const workExecutionData = {
        workOrderId: workOrder?.id,
        executionStartDate: executionStartDate,
        executionEndDate: executionEndDate,
        isSelfUpdated: true,
        // updateType: "Progress",
        lineUpdates: lineUpdates,
        submissionComments: values.notes || "Work execution update submitted",
        filesData: filesData,
      };

      // Prepare FormData for multipart request
      const formData = new FormData();
      formData.append("workExecution", JSON.stringify(workExecutionData));

      // Add files to form data
      if (fileList.length > 0) {
        fileList.forEach((file, index) => {
          formData.append("files", file.originFileObj || file);
        });
      }

      // Call the API
      const response: any = await workExecutionApis.selfCreateWorkExecution(
        formData
      );

      if (response && response.status) {
        message.success("Work execution submitted for approval successfully!");

        setExecutionModalVisible(false);
        form.resetFields();
        setFileList([]);

        // Reset work lines
        const resetLines = workLines.map((line) => ({
          ...line,
          executedQuantity: 0,
          remarks: "",
        }));
        setWorkLines(resetLines);

        // Navigate back after successful submission
        setTimeout(() => {
          const searchParams = new URLSearchParams(location.search);
          const flowType = searchParams.get("flowType");
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(
            `${process.env.PUBLIC_URL}/work-execution-under-execution${flowTypeQuery}`
          );
        }, 1500);
      } else {
        throw new Error(response?.message || "Failed to submit work execution");
      }
    } catch (error) {
      console.error("Error submitting work execution:", error);
      message.error("Failed to submit work execution. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadFile = (file: any) => {
    if (file.filePath) {
      // Open the file URL in a new tab to download
      window.open(file.filePath, "_blank");
    } else {
      Swal.fire("Info", `Downloading ${file.fileName}...`, "info");
    }
  };

  const viewFile = (file: any) => {
    if (file.filePath) {
      // Open the file URL in a new tab to view
      window.open(file.filePath, "_blank");
    } else {
      Swal.fire("Info", `Opening ${file.fileName}...`, "info");
    }
  };

  // Helper function to get file icon based on file type
  const getFileIcon = (fileType: string) => {
    if (fileType.includes("image")) return "🖼️";
    if (fileType.includes("pdf")) return "📄";
    if (fileType.includes("word")) return "📝";
    if (fileType.includes("excel") || fileType.includes("sheet")) return "📊";
    return "📎";
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Draft: "secondary",
      "Pending Approval": "warning",
      Approved: "success",
      Rejected: "danger",
      "Changes Requested": "warning",
      Released: "success",
      "Contractor Confirmed": "primary",
      "In Execution": "info",
      "On Hold": "warning",
      Completed: "success",
      Closed: "secondary",
      Cancelled: "danger",
    };
    return statusColors[status] || "secondary";
  };

  const getSourceColor = (source: WorkOrderLineSource) => {
    const colors = {
      [WorkOrderLineSource.ProjectManagement]: "blue",
      [WorkOrderLineSource.WorkBOQ]: "green",
      [WorkOrderLineSource.NewWorkLine]: "orange",
    };
    return colors[source] || "default";
  };

  // Work Order Lines Table Columns - conditionally show execution columns based on status
  const workOrderLinesColumns: TableColumnsType<any> = useMemo(() => {
    // Check if any line is from Project Management source (to show Task Rate column)
    const hasPMLines = workOrder?.workOrderLines?.some(
      (line: any) =>
        line.source === WorkOrderLineSource.ProjectManagement ||
        line.source === "Project Tasks"
    );

    // Check if any line has executionStatus !== "Not Started" to show execution columns
    const hasExecutionStarted = workOrder?.workOrderLines?.some(
      (line: any) =>
        line.executionStatus && line.executionStatus !== "Not Started"
    );

    const baseColumns: TableColumnsType<any> = [
      // {
      //   title: "#",
      //   key: "#",
      //   width: 50,
      //   fixed: "left",
      //   render: (text, record, index) => index + 1,
      // },
      // {
      //   title: "Line #",
      //   dataIndex: "lineNumber",
      //   key: "lineNumber",
      //   width: 80,
      //   fixed: "left",
      // },
      {
        title: "Task Title",
        dataIndex: "taskTitle",
        key: "taskTitle",
        width: 300,
        render: (text: string, record: WorkOrderLine) => (
          <div>
            <div className="fw-bold">{text}</div>
            {record.taskDescription && (
              <small className="text-muted">{record.taskDescription}</small>
            )}
          </div>
        ),
      },
      {
        title: "Category",
        dataIndex: "workCategoryName",
        key: "workCategoryName",
        width: 220,
        render: (text: string, record: WorkOrderLine) => (
          <div>
            <div>{text}</div>
            {record.subCategory && (
              <small className="text-muted">{record.subCategory}</small>
            )}
          </div>
        ),
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        width: 250,
        render: (quantity: number, record: any) =>
          `${formatDecimal(quantity)} ${record?.uomName}`,
      },
    ];

    // Conditionally add execution quantity columns only when execution has started
    if (hasExecutionStarted) {
      baseColumns.push(
        {
          title: "Executed Qty (Till Date)",
          dataIndex: "totalExecutedQty",
          key: "totalExecutedQty",
          width: 250,
          align: "right",
          render: (qty, record) => (
            <div>
              <div className="fw-bold">{qty?.toLocaleString("en-IN")}</div>
              <div className="text-muted small">{record.uomName}</div>
            </div>
          ),
        },
        {
          title: "Executed Qty (Submitted)",
          dataIndex: "totalApprovalExecutedQty",
          key: "totalApprovalExecutedQty",
          width: 250,
          align: "right",
          render: (qty, record) => (
            <div>
              <div className="fw-bold text-primary">
                {qty?.toLocaleString("en-IN")}
              </div>
              <div className="text-muted small">{record.uomName}</div>
            </div>
          ),
        },
        {
          title: "Billed Qty",
          dataIndex: "totalBilledQty",
          key: "totalBilledQty",
          width: 200,
          align: "right",
          render: (qty, record) => (
            <div>
              <div className="fw-bold text-success">
                {qty?.toLocaleString("en-IN") || 0}
              </div>
              <div className="text-muted small">{record.uomName}</div>
            </div>
          ),
        },
        {
          title: "On Hold Qty",
          dataIndex: "totalOnHoldQty",
          key: "totalOnHoldQty",
          width: 200,
          align: "right",
          render: (qty, record) => (
            <div>
              <div className="fw-bold text-warning">
                {qty?.toLocaleString("en-IN") || 0}
              </div>
              <div className="text-muted small">{record.uomName}</div>
            </div>
          ),
        }
      );
    }

    // Add remaining columns
    baseColumns.push(
      // {
      //   title: "UOM",
      //   dataIndex: "uomName",
      //   key: "uomName",
      //   width: 100,
      // },
      // Show Task Rate only for Project Management source
      // ...(hasPMLines
      //   ? [
      //       {
      //         title: "Task Rate",
      //         key: "taskRate",
      //         width: 120,
      //         render: (_: any, record: any) => {
      //           if (record.source === WorkOrderLineSource.ProjectManagement || record.source === "Project Management") {
      //             const taskRate = Number(record.taskRate) || 0;
      //             return (
      //               <div style={{ textAlign: "right" }}>
      //                 <span>₹{taskRate.toLocaleString("en-IN")}</span>
      //               </div>
      //             );
      //           }
      //           return <span className="text-muted">-</span>;
      //         },
      //       },
      //     ]
      //   : []),
      {
        title: hasPMLines ? "Execution Rate" : "Rate",
        dataIndex: "rate",
        key: "rate",
        width: 180,
        render: (rate: number, record: any) => {
          const hasRemarks =
            (record.source === WorkOrderLineSource.ProjectManagement ||
              record.source === "Project Tasks") &&
            record.remarks;

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>₹{rate?.toLocaleString("en-IN")}</span>
              {hasRemarks && (
                <Tooltip title={record.remarks} placement="top">
                  <InfoCircleOutlined
                    style={{
                      color: "#1890ff",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Tax",
        key: "tax",
        width: 180,
        render: (_: any, record: any) => (
          <div>
            {record.taxTypeName && (
              <div className="small">{record.taxTypeName}</div>
            )}
            {(record.sgstRate > 0 ||
              record.cgstRate > 0 ||
              record.igstRate > 0) && (
                <div className="small text-muted">
                  {record.sgstRate > 0 && record.cgstRate > 0 ? (
                    <>
                      SGST: {record.sgstRate}% + CGST: {record.cgstRate}%
                    </>
                  ) : record.igstRate > 0 ? (
                    <>IGST: {record.igstRate}%</>
                  ) : (
                    <>{record.taxPercentage}%</>
                  )}
                </div>
              )}
            <div className="fw-bold">
              ₹
              {record.taxValue?.toLocaleString("en-IN") ||
                record.taxAmount?.toLocaleString("en-IN") ||
                0}
            </div>
          </div>
        ),
      },
      {
        title: "Total Amount",
        key: "lineValue",
        width: 140,
        render: (_: any, record: any) =>
          `₹${record.lineValue?.toLocaleString("en-IN")}`,
      },
      {
        title: "Source",
        dataIndex: "source",
        key: "source",
        width: 140,
        render: (source: string) => (
          <Tag color={getSourceColor(source as WorkOrderLineSource)}>
            {source}
          </Tag>
        ),
      },
      {
        title: "Location",
        key: "location",
        width: 200,
        render: (_: any, record: any) => {
          // Helper function to check if a value is meaningful (not null, undefined, or empty string)
          const hasValue = (val: any): boolean => {
            if (val === null || val === undefined) return false;
            const str = String(val).trim();
            return str !== "" && str !== "null" && str !== "undefined";
          };

          // Show only locationName
          const locationName = hasValue(record.locationName)
            ? String(record.locationName).trim()
            : "-";

          return <span className="fw-bold">{locationName}</span>;
        },
      },
      {
        title: "Budget Details",
        key: "budgetDetails",
        width: 200,
        render: (_: any, record: any) => (
          <div>
            {record.budgetLineName ? (
              <div>
                <div className="fw-bold small text-primary">
                  {record.budgetLineName}
                </div>
                <div className="small text-success">
                  Allocated: ₹{record.budgetAllocation?.toLocaleString("en-IN") || 0}
                </div>
                {/* {record.budgetLineId && (
                  <div className="small text-muted">
                    Budget Line ID: {record.budgetLineId}
                  </div>
                )} */}
              </div>
            ) : (
              <div className="small text-muted">No budget allocation</div>
            )}
          </div>
        ),
      },
      {
        title: "Docs",
        key: "docs",
        width: 100,
        render: (_: any, record: any) => {
          // Get files from uploadedDocs (mapped from line.dms)
          const lineFiles: any[] = record.uploadedDocs || [];

          if (lineFiles.length === 0) {
            return <span className="text-muted">-</span>;
          }

          const content = (
            <div style={{ minWidth: "200px" }}>
              {lineFiles.map((file: any, index: number) => {
                const fileName =
                  file.fileName ||
                  file.name ||
                  file.response?.fileName ||
                  `File ${index + 1}`;
                const truncatedName =
                  fileName.length > 25
                    ? `${fileName.substring(0, 25)}...`
                    : fileName;
                return (
                  <div
                    key={file.docId || file.id || index}
                    style={{
                      padding: "8px 0",
                      borderBottom:
                        index < lineFiles.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <EyeOutlined
                      style={{ color: "#1890ff", fontSize: "16px" }}
                    />
                    <Tooltip
                      title={fileName}
                      placement="top"
                      overlayStyle={{ zIndex: 9999 }}
                      getPopupContainer={(trigger) =>
                        trigger.parentElement || document.body
                      }
                    >
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          const fileUrl =
                            file.filePath ||
                            file.url ||
                            file.fileUrl ||
                            file.apiFileData?.fileUrl ||
                            file.apiFileData?.url ||
                            file.apiFileData?.filePath ||
                            file.apiFileData?.documentUrl ||
                            file.response?.fileUrl ||
                            file.response?.url ||
                            file.response?.filePath ||
                            file.response?.documentUrl;

                          if (fileUrl) {
                            window.open(fileUrl, "_blank");
                          } else {
                            // Try to construct URL from docId if available
                            const docId =
                              file.docId ||
                              file.dmsId ||
                              file.id ||
                              file.apiFileData?.dmsId ||
                              file.apiFileData?.fileId ||
                              file.apiFileData?.docId ||
                              file.response?.fileId ||
                              file.response?.dmsId ||
                              file.response?.docId;

                            if (
                              docId &&
                              (typeof docId === "number" ||
                                (typeof docId === "string" &&
                                  !docId.includes("-") &&
                                  !docId.startsWith("wol-")))
                            ) {
                              // Construct download URL - try common API patterns
                              const baseUrl =
                                process.env.REACT_APP_API_BASE_URL ||
                                window.location.origin;
                              // Try different URL patterns
                              const possibleUrls = [
                                `${baseUrl}/api/dms/${docId}/download`,
                                `${baseUrl}/api/dms/${docId}/view`,
                                `${baseUrl}/api/files/${docId}/download`,
                                `${baseUrl}/api/files/${docId}`,
                              ];

                              // Try first URL pattern
                              window.open(possibleUrls[0], "_blank");
                            } else {
                              message.info(
                                "File URL not available. Please contact support."
                              );
                            }
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          color: "#1890ff",
                          cursor: "pointer",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "200px",
                        }}
                      >
                        {truncatedName}
                      </span>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          );

          return (
            <Popover
              content={content}
              title="Documents"
              trigger="hover"
              placement="left"
            >
              <AntdButton
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                View
              </AntdButton>
            </Popover>
          );
        },
      }
    );

    return baseColumns;
  }, [workOrder?.status, workOrder?.workOrderLines]);

  // Execution Lines Table Columns
  const executionLinesColumns: TableColumnsType<any> = [
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
      fixed: "left",
    },
    {
      title: "Work Category",
      dataIndex: "workCategoryName",
      key: "workCategoryName",
      width: 150,
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: 80,
    },
    {
      title: "Approved Qty",
      dataIndex: "approvedQuantity",
      key: "approvedQuantity",
      width: 120,
      render: (qty: number) => formatDecimal(qty),
    },
    {
      title: "Allocated Qty",
      dataIndex: "allocatedQuantity",
      key: "allocatedQuantity",
      width: 120,
      render: (qty: number) => formatDecimal(qty),
    },
    {
      title: "Remaining Qty",
      dataIndex: "remainingQuantity",
      key: "remainingQuantity",
      width: 120,
      render: (qty: number) => (
        <span style={{ color: qty > 0 ? "green" : "red" }}>
          {formatDecimal(qty)}
        </span>
      ),
    },
    {
      title: "Executed Qty",
      dataIndex: "executedQuantity",
      key: "executedQuantity",
      width: 150,
      render: (_: any, record: any, index: number) => (
        <Input
          type="number"
          min={0}
          max={record.remainingQuantity}
          value={record.executedQuantity}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            const updatedLines = [...workLines];
            updatedLines[index].executedQuantity = value;
            setWorkLines(updatedLines);
          }}
          placeholder="Enter qty"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 120,
      render: (rate: number) => `₹${rate?.toLocaleString("en-IN")}`,
    },
    {
      title: "Line Value",
      key: "lineValue",
      width: 120,
      render: (_: any, record: any) => {
        const lineValue = record.executedQuantity * record.rate;
        return `₹${lineValue?.toLocaleString("en-IN")}`;
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 200,
      render: (_: any, record: any, index: number) => (
        <Input.TextArea
          value={record.remarks}
          onChange={(e) => {
            const updatedLines = [...workLines];
            updatedLines[index].remarks = e.target.value;
            setWorkLines(updatedLines);
          }}
          placeholder="Enter remarks (optional)"
          rows={2}
          style={{ width: "100%" }}
        />
      ),
    },
  ];

  const getStatusMapper = (status: string) => {
    const statusMap: { [key: string]: { title: string; color: string } } = {
      Draft: { title: "Draft", color: "secondary" },
      "Pending Approval": { title: "Pending Approval", color: "warning" },
      Approved: { title: "Approved", color: "success" },
      Rejected: { title: "Rejected", color: "danger" },
      "Changes Requested": { title: "Changes Requested", color: "warning" },
      Released: { title: "Released", color: "success" },
      "Contractor Confirmed": {
        title: "Contractor Confirmed",
        color: "primary",
      },
      "In Execution": { title: "In Execution", color: "info" },
      "On Hold": { title: "On Hold", color: "warning" },
      Completed: { title: "Completed", color: "success" },
      Closed: { title: "Closed", color: "secondary" },
      Cancelled: { title: "Cancelled", color: "danger" },
    };
    return statusMap[status] || { title: status, color: "secondary" };
  };

  // Check if current user can approve based on approval hierarchy
  const canCurrentUserApprove = () => {
    console.log("workOrder.approvals1");

    if (
      !workOrder ||
      !currentUser ||
      !workOrder.approvals ||
      workOrder.approvals.length === 0
    ) {
      return false;
    }
    console.log("workOrder.approvals::::");

    let currentUserId = currentUser.userId || currentUser.id;
    if (!currentUserId) {
      return false;
    }

    // Extract numeric ID if it's in format "user-123"
    if (
      typeof currentUserId === "string" &&
      currentUserId.startsWith("user-")
    ) {
      currentUserId = parseInt(currentUserId.replace("user-", ""));
    } else if (typeof currentUserId === "string") {
      currentUserId = parseInt(currentUserId);
    }

    console.log("workOrder.approvals::::", workOrder.approvals, currentUserId);

    // Check if current user is in the approval hierarchy
    const userApproval = workOrder.approvals.find((approval: any) => {
      const approvalUserId =
        typeof approval.userId === "string"
          ? parseInt(approval.userId)
          : approval.userId;
      console.log(
        "workOrder.approvals",
        approvalUserId,
        approvalUserId === currentUserId
      );
      return approvalUserId === currentUserId;
    });
    console.log("workOrder.approvals::::", userApproval);
    if (!userApproval) {
      console.log("User not found in approval hierarchy");
      return false;
    }
    console.log("workOrder.approvals:", workOrder.approvals);
    console.log("userApproval found:", userApproval);

    // // Sort approvals by level to determine hierarchy order
    // const sortedApprovals = [...workOrder.approvals].sort((a: any, b: any) => {
    //   const levelA = parseInt(a.level.replace("level", "")) || 0;
    //   const levelB = parseInt(b.level.replace("level", "")) || 0;
    //   return levelA - levelB;
    // });

    // // Find the current user's position in the hierarchy
    // const userIndex = sortedApprovals.findIndex(
    //   (approval: any) => approval.userId === currentUserId
    // );

    // // Check if all previous approvers have approved
    // for (let i = 0; i < userIndex; i++) {
    //   const prevApproval = sortedApprovals[i];
    //   if (prevApproval.status !== "Approved") {
    //     // Previous approver hasn't approved yet
    //     return false;
    //   }
    // }

    // Check if the current user hasn't already approved/rejected
    if (
      userApproval.status === "Approved" ||
      userApproval.status === "Rejected"
    ) {
      console.log("User has already approved/rejected");
      return false;
    }

    return true;
  };

  if (loading) {
    return (
      <>
        <Head title="Work Order Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workOrder) {
    return (
      <>
        <Head title="Work Order Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work Order not found" />
          </div>
        </Content>
      </>
    );
  }

  const openExecutionModal = () => {
    // Navigate to the new Work Execution Update page
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    navigate(`/work-execution-update/${workOrder.id}${flowTypeQuery}`);
  };

  const openOnHoldExecutionPage = () => {
    // Navigate to the OnHold Execution page
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    navigate(`/work-execution-hold/${workOrder.id}${flowTypeQuery}`);
  };

  const openReleaseExecutionPage = () => {
    // Navigate to the Release Execution page
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    navigate(`/work-execution-release/${workOrder.id}${flowTypeQuery}`);
  };

  const handleMarkAsCompleted = async () => {
    try {
      // Show confirmation dialog with remarks input
      const result = await Swal.fire({
        title: "Mark Work Order as Completed?",
        html: `
          <p>Are you sure you want to mark this work order as completed?</p>
          <textarea
            id="completion-remarks"
            placeholder="Enter completion remarks (optional)"
            style="width: 100%; height: 80px; margin-top: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
          ></textarea>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, Mark as Completed",
        cancelButtonText: "Cancel",
        didOpen: () => {
          const popup = Swal.getPopup();
          if (popup) {
            popup.style.width = "600px";
          }
        },
        preConfirm: () => {
          const remarks = (
            document.getElementById("completion-remarks") as HTMLTextAreaElement
          )?.value;
          return remarks || "Work completed successfully";
        },
      });

      if (result.isConfirmed) {
        const requestBody = {
          workOrderId: workOrder?.id,
          completionDate: dayjs().format("YYYY-MM-DD"),
          completionRemarks: result.value,
        };

        console.log("Marking work order as completed:", requestBody);

        const response: any = await workOrderApis.completeWorkOrder(
          requestBody
        );

        if (response && response.status) {
          Swal.fire(
            "Completed!",
            response.message ||
            "Work Order has been marked as completed successfully",
            "success"
          ).then(() => {
            // Reload the work order data to reflect the new status
            if (id) {
              loadWorkOrderData(id);
            }
          });
        } else {
          throw new Error(
            response?.message || "Failed to mark work order as completed"
          );
        }
      }
    } catch (error: any) {
      console.error("Error marking work order as completed:", error);
      Swal.fire(
        "Error!",
        error.message ||
        "Failed to mark work order as completed. Please try again.",
        "error"
      );
    }
  };

  const handleAddRiskClass = () => {
    // Navigate to Add Riskflags page or open modal
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    navigate(`/work-order-riskflags/${workOrder.id}${flowTypeQuery}`, {
      state: { fromWorkOrderView: true },
    });
  };

  const handleAddAdvance = () => {
    // Navigate to Add Advance page or open modal
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    navigate(`/work-order-advance/${workOrder.id}${flowTypeQuery}`, {
      state: { fromWorkOrderView: true },
    });
  };

  const statusInfo = getStatusMapper(workOrder.status);

  // Calculate totals
  const subtotal =
    workOrder.workOrderLines?.reduce(
      (sum, line) => sum + line.quantity * line.rate,
      0
    ) || 0;
  const lineLevelTaxAmount =
    workOrder.workOrderLines?.reduce(
      (sum, line) => sum + (line.taxAmount || 0),
      0
    ) || 0;

  // Calculate GST slab-wise breakdown
  const gstSlabBreakdown: {
    [key: string]: {
      cgstRate: number;
      sgstRate: number;
      igstRate: number;
      taxAmount: number;
    };
  } = {};

  if (workOrder.enableTax && workOrder.workOrderLines) {
    workOrder.workOrderLines.forEach((line: any) => {
      if (
        line.taxPercentage &&
        line.taxPercentage > 0 &&
        line.taxAmount &&
        line.taxAmount > 0
      ) {
        const rate = line.taxPercentage;
        const rateKey = `${rate}%`;

        if (!gstSlabBreakdown[rateKey]) {
          gstSlabBreakdown[rateKey] = {
            cgstRate: 0,
            sgstRate: 0,
            igstRate: 0,
            taxAmount: 0,
          };
        }

        // Use actual GST amounts from API response
        if (line.igstAmount && line.igstAmount > 0) {
          gstSlabBreakdown[rateKey].igstRate += line.igstAmount;
        } else {
          gstSlabBreakdown[rateKey].sgstRate += line.cgstAmount || 0;
          gstSlabBreakdown[rateKey].cgstRate += line.sgstAmount || 0;
        }
        gstSlabBreakdown[rateKey].taxAmount += line.taxAmount;
      }
    });
  }

  // Calculate header level tax amounts (TDS and TCS)
  // TDS is calculated on subtotal only
  // const tdsAmount =
  //   workOrder.tdsRate && subtotal > 0
  //     ? (subtotal * workOrder.tdsRate) / 100
  //     : 0;
  // TCS is calculated on subtotal + GST (lineLevelTaxAmount)
  const subtotalWithGST = subtotal + lineLevelTaxAmount;
  // const tcsAmount =
  //   workOrder.tcsRate && subtotalWithGST > 0
  //     ? (subtotalWithGST * workOrder.tcsRate) / 100
  //     : 0;

  const totalTaxAmount = lineLevelTaxAmount;
  const grossTotal = subtotal + totalTaxAmount;

  // Calculate payment conditions deductions (Retention + On Hold, excluding Mobilization advance)
  const paymentDeductions =
    workOrder?.paymentConditions &&
      isPaymentConditionsArray(workOrder.paymentConditions)
      ? (workOrder.paymentConditions as PaymentCondition[]).reduce(
        (sum: number, condition: PaymentCondition) =>
          condition.conditionType !== "Mobilization"
            ? sum + (condition.calculatedAmount || 0)
            : sum,
        0
      )
      : 0;

  // Net Payable = Gross Total - TDS - Payment Deductions (Retention + On Hold)
  const netPayable = grossTotal;

  const ApprovalTimelineContent = () => {
    const approvalsData = workOrder.approvals;
    if (!approvalsData || approvalsData?.length === 0)
      return <Badge color="outline-danger">No</Badge>;
    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status?.toLowerCase() == "approved";
              const isRejected = approval.status?.toLowerCase() == "rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.approverName}
                  <div className="designation">{approval?.approverRole}</div>
                  {approval?.actionDate && (
                    <div className="designation">
                      {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                  {approval.approvalStage && (
                    <div className="text-muted small">
                      <Tag
                        color="blue"
                        style={{ fontSize: "10px" }}
                      >
                        {approval.approvalStage}
                      </Tag>
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer"
        }}>
          <span
            style={{
              color: "#2263b3",
              // textDecoration: "underline",
            }}
          >
            <Badge color="outline-success">Yes</Badge>
          </span>

          {isStrictMode && (
            <Badge
              color="warning"
              style={{
                fontSize: "11px",
                cursor: "pointer",
                marginLeft: "4px"
              }}
              title="Sequential approvals required, Approvals must follow the sequence"
            >
              Strict
            </Badge>
          )}
        </div>
      </Popover>
    );
  };

  return (
    <>
      <Head title={`Work Order - ${workOrder.workOrderNumber}`} />
      <Content>
        <div className="work-order-view-wrapper">
          <Card className="card-bordered">
            <div className="card-aside-wrap">
              <div className="card-inner card-inner-lg">
                {sm && mobileView && (
                  <div
                    className="toggle-overlay"
                    onClick={() => setSm(!sm)}
                  ></div>
                )}
                <BlockHead size="lg">
                  <BlockBetween>
                    <BlockHeadContent>
                      <BlockTitle tag="h4">
                        <span>
                          {workOrder.workOrderNumber} | {statusInfo.title}
                        </span>
                        {/* {isAmendmentView && amendmentData && (
                        <Badge
                          color={getChangeRequestStatusColor(
                            amendmentData.changeRequestStatus
                          )}
                          className="ml-2"
                        >
                          {amendmentData.changeRequestStatus}
                        </Badge>
                      )} */}
                      </BlockTitle>
                    </BlockHeadContent>
                    <div className="d-flex align-items-center">
                      {/* Back Button - Always visible */}
                      <Button
                        color="light"
                        outline
                        className="bg-white me-2"
                        onClick={() => {
                          const searchParams = new URLSearchParams(
                            location.search
                          );
                          const flowType = searchParams.get("flowType");
                          const flowTypeQuery = flowType
                            ? `?flowType=${flowType}`
                            : "";

                          // Check if we came from Add Riskflags or Add Advance pages
                          const cameFromRiskflagsOrAdvance =
                            location.state?.fromRiskflagsOrAdvance === true ||
                            document.referrer?.includes(
                              "/work-order-riskflags/"
                            ) ||
                            document.referrer?.includes("/work-order-advance/");

                          if (
                            isAmendmentView ||
                            workOrder.status === "Changes Requested"
                          ) {
                            navigate(
                              `/work-order-contractor-approvals${flowTypeQuery}`
                            );
                          } else if (cameFromRiskflagsOrAdvance) {
                            // If we came from Add Riskflags/Advance, go to work orders list
                            navigate(`/view-work-orders${flowTypeQuery}`);
                          } else {
                            navigate(-1);
                          }
                        }}
                      >
                        <Icon name="arrow-left" />
                        <span>Back</span>
                      </Button>
                      {/* Actions Dropdown Menu */}
                      <Dropdown
                        isOpen={actionsDropdownOpen}
                        toggle={() =>
                          setActionsDropdownOpen(!actionsDropdownOpen)
                        }
                        className="me-2"
                      >
                        <DropdownToggle
                          color="primary"
                          className="btn-icon"
                          style={{ padding: "8px 12px" }}
                        >
                          <MoreOutlined style={{ fontSize: "18px" }} />
                        </DropdownToggle>
                        <DropdownMenu end>
                          {/* Export PDF - Always visible */}
                          <DropdownItem
                            onClick={() => {
                              setPdfLoading(true);
                              const pdfButton = document.querySelector(
                                "[data-pdf-generator-trigger]"
                              ) as HTMLButtonElement;
                              if (pdfButton) pdfButton.click();
                            }}
                          >
                            <FilePdfOutlined className="me-2" />
                            Export PDF
                          </DropdownItem>

                          {/* Add Riskflags - Exclude statuses: Completed, Cancelled, Draft, On Hold, Closed and executionStatus: Not Started */}
                          {workOrder.status !== "Completed" &&
                            workOrder.status !== "Cancelled" &&
                            workOrder.status !== "Draft" &&
                            workOrder.status !== "On Hold" &&
                            workOrder.status !== "Closed" &&
                            workOrder.executionStatus !== "Not Started" && (
                              <DropdownItem onClick={handleAddRiskClass}>
                                <WarningOutlined className="me-2" />
                                Add Riskflags
                              </DropdownItem>
                            )}

                          {/* Add Advance - Except Completed, Cancelled, Draft, On Hold, Closed status */}
                          {workOrder.status !== "Completed" &&
                            workOrder.status !== "Cancelled" &&
                            workOrder.status !== "Draft" &&
                            workOrder.status !== "On Hold" &&
                            workOrder.status !== "Closed" &&
                            workOrder.status !== "Approved" && (
                              <DropdownItem onClick={handleAddAdvance}>
                                <PlusCircleOutlined className="me-2" />
                                Add Advance
                              </DropdownItem>
                            )}

                          {/* Edit Work Order */}
                          {(isAmendmentView ||
                            workOrder.status === "Draft" ||
                            workOrder.status === "Changes Requested" ||
                            workOrder.status === "Approved" ||
                            workOrder.status === "Amended") && (
                              <DropdownItem onClick={handleEdit}>
                                <EditOutlined className="me-2" />
                                Edit Work Order
                              </DropdownItem>
                            )}

                          {/* Duplicate - Draft only */}
                          {!isAmendmentView && workOrder.status === "Draft" && (
                            <DropdownItem onClick={handleDuplicate}>
                              <CopyOutlined className="me-2" />
                              Duplicate
                            </DropdownItem>
                          )}

                          {/* Delete - Draft only */}
                          {!isAmendmentView && workOrder.status === "Draft" && (
                            <DropdownItem
                              onClick={handleDelete}
                              className="text-danger"
                            >
                              <DeleteOutlined className="me-2" />
                              Delete
                            </DropdownItem>
                          )}

                          {/* Approve - Pending Approval */}
                          {!isAmendmentView &&
                            workOrder.status === "Pending Approval" &&
                            allowedActions?.approve === 1 &&
                            !hasUserAlreadyApproved &&
                            canUserApproveReject() && (
                              <>
                                <DropdownItem
                                  onClick={() => {
                                    // Open signature selection modal first for approve
                                    setActiveSignatureContext('APPROVE');
                                    setIsModalApproveVisible(true);
                                  }}
                                  disabled={disableApprove}
                                  className="text-success"
                                >
                                  <CheckOutlined className="me-2" />
                                  {disableApprove ? "Approve..." : "Approve"}
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() => {
                                    // Open reject modal
                                    setActiveSignatureContext('REJECT');
                                    setIsModalRejectedVisible(true);
                                  }}
                                  disabled={disableReject}
                                  className="text-danger"
                                >
                                  <CloseOutlined className="me-2" />
                                  {disableReject ? "Reject..." : "Reject"}
                                </DropdownItem>
                              </>
                            )}

                          {/* Release Work Order - Approved */}
                          {workOrder.status === "Approved" &&
                            !isAmendmentView &&
                            workOrder.contractorApplicationAccess === true && (
                              <DropdownItem
                                onClick={handleReleaseWorkOrder}
                                className="text-success"
                              >
                                <PlayCircleOutlined className="me-2" />
                                Release Work Order
                              </DropdownItem>
                            )}

                          {/* Update Work Execution */}
                          {(workOrder.status === "In Execution" ||
                            workOrder.status === "Approved" ||
                            workOrder.status === "Amended" ||
                            workOrder.status ===
                            "Pending Execution Approval") &&
                            (!workOrder.plannedStartDate ||
                              !dayjs()
                                .startOf("day")
                                .isBefore(
                                  dayjs(workOrder.plannedStartDate).startOf(
                                    "day"
                                  )
                                )) && (
                              <DropdownItem onClick={openExecutionModal}>
                                <SyncOutlined className="me-2" />
                                Update Work Execution
                              </DropdownItem>
                            )}

                          {/* Mark as Completed - Open work type */}
                          {workOrder.workType === "Open" &&
                            workOrder.executionStatus ===
                            "Partially Executed" &&
                            workOrder.status === "In Execution" && (
                              <DropdownItem
                                onClick={handleMarkAsCompleted}
                                className="text-warning"
                              >
                                <CheckCircleOutlined className="me-2" />
                                Mark as Completed
                              </DropdownItem>
                            )}

                          {/* OnHold Execution - In Execution status */}
                          {(workOrder.status === "In Execution" ||
                            workOrder.executionStatus ===
                            "Partially Executed") && (
                              <DropdownItem onClick={openOnHoldExecutionPage}>
                                <PauseCircleOutlined className="me-2" />
                                OnHold Execution
                              </DropdownItem>
                            )}

                          {/* Release Execution - In Execution status */}
                          {(workOrder.status === "In Execution" ||
                            workOrder.executionStatus ===
                            "Partially Executed") && (
                              <DropdownItem onClick={openReleaseExecutionPage}>
                                <UnlockOutlined className="me-2" />
                                Release Execution
                              </DropdownItem>
                            )}
                        </DropdownMenu>
                      </Dropdown>

                      {/* Hidden PDF Generator - triggered from dropdown */}
                      <div
                        style={{
                          position: "absolute",
                          left: "-9999px",
                          top: 0,
                          zIndex: -1000,
                        }}
                      >
                        <WorkOrderPdfGenerator
                          workOrderData={workOrder}
                          orgName={workOrder.orgName}
                          triggerAttribute="data-pdf-generator-trigger"
                          onPdfGenerated={() => setPdfLoading(false)}
                        />
                      </div>
                    </div>
                    <BlockHeadContent className="align-self-start d-lg-none">
                      <Button
                        className={`toggle btn btn-icon btn-trigger mt-n1 ${sm ? "active" : ""
                          }`}
                        onClick={() => setSm(!sm)}
                      >
                        <Icon name="menu-alt-r" />
                      </Button>
                    </BlockHeadContent>
                  </BlockBetween>
                </BlockHead>

                <Alert
                  color={"danger"}
                  isOpen={workOrder.status === "Rejected"}
                >
                  <strong>Rejected: </strong>
                  {(workOrder as any).rejectionComments || "No reason provided"}
                </Alert>

                <Alert
                  color={"warning"}
                  isOpen={workOrder.status === "Changes Requested"}
                >
                  <strong>Changes Requested: </strong>
                  This work order requires amendments based on review feedback.
                  Please make the necessary changes and resubmit for approval.
                </Alert>

                {/* Amendment specific alert */}
                {/* {isAmendmentView && amendmentData && (
                <Alert color="warning">
                  <strong>Change Request Pending Review</strong>
                  <br />
                  This work order has a pending change request submitted by{" "}
                  {amendmentData.changeRequestedBy} on{" "}
                  {dayjs(amendmentData.changeRequestDate).format("DD/MM/YYYY")}.
                  Please review the proposed changes and take appropriate
                  action.
                </Alert>
              )} */}

                <div className="content clearfix">
                  <AntCollapse
                    defaultActiveKey={["1", "2", "3", "4", "5", "6", "7"]}
                  >
                    {/* Section A: General Information */}
                    <Panel
                      header={
                        <div className="d-flex align-items-center justify-content-between">
                          <>📋 General Information</>
                        </div>
                      }
                      key="1"
                    >
                      <Descriptions bordered size="small" column={2}>
                        <Descriptions.Item label="Work Order Number">
                          {workOrder.workOrderNumber}
                        </Descriptions.Item>
                        <Descriptions.Item label="Contract Date">
                          {workOrder.contractDate
                            ? dayjs(workOrder.contractDate).format(
                              "DD-MMM-YYYY"
                            )
                            : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Work Order Title" span={2}>
                          {workOrder.workOrderTitle}
                        </Descriptions.Item>
                        <Descriptions.Item label="Site">
                          {workOrder.siteName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Project">
                          {workOrder.projectName || "Not selected"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Contract Type">
                          {workOrder.contractType}
                        </Descriptions.Item>
                        <Descriptions.Item label="Contract With">
                          {workOrder.contractWith}
                        </Descriptions.Item>
                        <Descriptions.Item
                          label={isClientFlow ? "Client" : "Contractor"}
                        >
                          {workOrder.contractorName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Work Order Owner">
                          {workOrder.workOrderOwnerName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Planned Start">
                          {workOrder.plannedStartDate
                            ? dayjs(workOrder.plannedStartDate).format(
                              "DD-MMM-YYYY"
                            )
                            : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Planned End">
                          {workOrder.plannedEndDate
                            ? dayjs(workOrder.plannedEndDate).format(
                              "DD-MMM-YYYY"
                            )
                            : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Progress" span={2}>
                          <Progress
                            percent={workOrder.progressPercent || 0}
                            size="small"
                            status={
                              (workOrder.progressPercent || 0) >= 100
                                ? "success"
                                : "active"
                            }
                            style={{ maxWidth: 300 }}
                          />
                        </Descriptions.Item>
                        <Descriptions.Item label="Approval Required">
                          <ApprovalTimelineContent />
                        </Descriptions.Item>
                        <Descriptions.Item label="Tax Enabled" span={2}>
                          <Tag color={workOrder.enableTax ? "blue" : "default"}>
                            {workOrder.enableTax
                              ? `Yes (${workOrder.taxApplicationLevel ||
                              "Not specified"
                              })`
                              : "No"}
                          </Tag>
                          {workOrder.enableTax &&
                            (workOrder.taxApplicationLevel === "Header" ||
                              workOrder.taxApplicationLevel === "Both") &&
                            workOrder.headerTaxName && (
                              <div className="mt-2">
                                <small className="text-muted">
                                  Header Tax Type:{" "}
                                </small>
                                <Tag color="cyan">
                                  {workOrder.headerTaxName}
                                </Tag>
                              </div>
                            )}
                          {workOrder.enableTax &&
                            (workOrder.taxApplicationLevel === "Both" ||
                              workOrder.taxApplicationLevel === "Header") && (
                              <div className="mt-2">
                                {workOrder.tdsTaxName && (
                                  <div>
                                    <small className="text-muted">TDS: </small>
                                    <Tag color="orange">
                                      {workOrder.tdsTaxName}
                                    </Tag>
                                    <small className="text-info">
                                      {" "}
                                      (Deducted from sub total)
                                    </small>
                                  </div>
                                )}
                                {workOrder.tcsTaxName && (
                                  <div>
                                    <small className="text-muted">TCS: </small>
                                    <Tag color="purple">
                                      {workOrder.tcsTaxName}
                                    </Tag>
                                    <small className="text-success">
                                      {" "}
                                      (Deducted from gross total)
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}
                          {/* {workOrder.budgetName && (
                          <div className="mt-2">
                            <small className="text-muted">Budget: </small>
                            <Tag color="green">{workOrder.budgetName}</Tag>
                          </div>
                        )} */}
                        </Descriptions.Item>
                      </Descriptions>

                      {workOrder.workOrderDescription && (
                        <div className="mt-3">
                          <strong>Description:</strong>
                          <p className="mt-1 text-muted">
                            {workOrder.workOrderDescription}
                          </p>
                        </div>
                      )}
                    </Panel>

                    {/* Section B: Work Order Lines */}
                    <Panel
                      header={`📋 Work Order Lines (${workOrder.workOrderLines?.length || 0
                        })`}
                      key="2"
                    >
                      <div className="mb-3">
                        <Tag color="blue">
                          {workOrder.workOrderLines?.length || 0} line(s)
                        </Tag>
                        {workOrder.budgetName && (
                          <Tag color="purple">
                            Budget : {workOrder.budgetName}
                          </Tag>
                        )}
                        {workOrder.sourceType === "New Work Line" &&
                          workOrder.segmentLevel && (
                            <Tag color="orange">
                              Work Level: {workOrder.segmentLevel}
                            </Tag>
                          )}
                        {workOrder.workType && (
                          <Tag color="pink">
                            Work Type: {workOrder.workType}
                          </Tag>
                        )}
                      </div>

                      <div className="table-responsive poTable">
                        <div
                          style={{
                            width: screenSize - 170 + "px",
                            margin: "auto",
                          }}
                        >
                          {!workOrder.workOrderLines ||
                            workOrder.workOrderLines.length === 0 ? (
                            <div className="text-center py-5">
                              <Empty description="No work order lines found" />
                            </div>
                          ) : (
                            <Table
                              className="customTable"
                              columns={workOrderLinesColumns}
                              dataSource={workOrder.workOrderLines}
                              scroll={{ x: 1500 }}
                              bordered
                              size="small"
                              pagination={false}
                              rowKey="id"
                              expandable={{
                                expandedRowRender: (record: any) => {
                                  if (
                                    !record.stages ||
                                    record.stages.length === 0
                                  )
                                    return null;

                                  const totalPercentage = record.stages.reduce(
                                    (sum: number, stage: any) =>
                                      sum + (stage.splitPercentage || 0),
                                    0
                                  );
                                  const totalRateAmount = record.stages.reduce(
                                    (sum: number, stage: any) =>
                                      sum + (stage.rate || 0),
                                    0
                                  );
                                  const totalAmount = record.stages.reduce(
                                    (sum: number, stage: any) =>
                                      sum +
                                      (stage.amount || stage.subtotal || 0),
                                    0
                                  );
                                  const totalAllocatedQty =
                                    record.stages.reduce(
                                      (sum: number, stage: any) =>
                                        sum + (stage.allocatedQty || 0),
                                      0
                                    );
                                  const totalExecutedQty = record.stages.reduce(
                                    (sum: number, stage: any) =>
                                      sum + (stage.totalExecutedQty || 0),
                                    0
                                  );
                                  const totalApprovalExecutedQty =
                                    record.stages.reduce(
                                      (sum: number, stage: any) =>
                                        sum +
                                        (stage.totalApprovalExecutedQty || 0),
                                      0
                                    );
                                  const totalBilledQty = record.stages.reduce(
                                    (sum: number, stage: any) =>
                                      sum + (stage.totalBilledQty || 0),
                                    0
                                  );
                                  const totalOnHoldQty = record.stages.reduce(
                                    (sum: number, stage: any) =>
                                      sum + (stage.totalOnHoldQty || 0),
                                    0
                                  );

                                  return (
                                    <div
                                      style={{
                                        padding: "12px 16px",
                                        backgroundColor: "#fafafa",
                                      }}
                                    >
                                      <div
                                        style={{
                                          marginBottom: 8,
                                          fontWeight: 600,
                                          color: "#1890ff",
                                        }}
                                      >
                                        <ScheduleOutlined
                                          style={{ marginRight: 8 }}
                                        />
                                        Schedule Breakdown (
                                        {record.stages.length} stages)
                                      </div>
                                      <Table
                                        dataSource={record.stages.map(
                                          (stage: any, idx: number) => ({
                                            ...stage,
                                            key: idx,
                                            uomName: record.uomName,
                                          })
                                        )}
                                        columns={[
                                          {
                                            title: "Stage",
                                            dataIndex: "stageTitle",
                                            key: "stageTitle",
                                            width: 200,
                                          },
                                          {
                                            title: "Percentage",
                                            dataIndex: "splitPercentage",
                                            key: "splitPercentage",
                                            width: 100,
                                            render: (val: number) => (
                                              <Tag color="blue">{val}%</Tag>
                                            ),
                                          },
                                          {
                                            title: "Stage Rate",
                                            dataIndex: "rate",
                                            key: "rate",
                                            width: 120,
                                            render: (val: number) => (
                                              <span style={{ fontWeight: 500 }}>
                                                ₹
                                                {val?.toLocaleString("en-IN", {
                                                  maximumFractionDigits: 2,
                                                })}
                                              </span>
                                            ),
                                          },
                                          {
                                            title: "Quantity",
                                            dataIndex: "allocatedQty",
                                            key: "allocatedQty",
                                            width: 120,
                                            align: "right",
                                            render: (
                                              qty: number,
                                              record: any
                                            ) => (
                                              <div>
                                                <div className="fw-bold">
                                                  {qty?.toLocaleString("en-IN") || 0}
                                                </div>
                                                <div className="text-muted small">
                                                  {record.uomName ||
                                                    record.uom ||
                                                    ""}
                                                </div>
                                              </div>
                                            ),
                                          },
                                          {
                                            title: "Executed Qty (Till Date)",
                                            dataIndex: "totalExecutedQty",
                                            key: "totalExecutedQty",
                                            width: 180,
                                            align: "right",
                                            render: (
                                              qty: number,
                                              record: any
                                            ) => (
                                              <div>
                                                <div className="fw-bold">
                                                  {qty?.toLocaleString("en-IN") || 0}
                                                </div>
                                                <div className="text-muted small">
                                                  {record.uomName ||
                                                    record.uom ||
                                                    ""}
                                                </div>
                                              </div>
                                            ),
                                          },
                                          {
                                            title: "Executed Qty (Submitted)",
                                            dataIndex:
                                              "totalApprovalExecutedQty",
                                            key: "totalApprovalExecutedQty",
                                            width: 180,
                                            align: "right",
                                            render: (
                                              qty: number,
                                              record: any
                                            ) => (
                                              <div>
                                                <div className="fw-bold text-primary">
                                                  {qty?.toLocaleString("en-IN") || 0}
                                                </div>
                                                <div className="text-muted small">
                                                  {record.uomName ||
                                                    record.uom ||
                                                    ""}
                                                </div>
                                              </div>
                                            ),
                                          },
                                          {
                                            title: "Billed Qty",
                                            dataIndex: "totalBilledQty",
                                            key: "totalBilledQty",
                                            width: 120,
                                            align: "right",
                                            render: (
                                              qty: number,
                                              record: any
                                            ) => (
                                              <div>
                                                <div className="fw-bold text-success">
                                                  {qty?.toLocaleString("en-IN") || 0}
                                                </div>
                                                <div className="text-muted small">
                                                  {record.uomName ||
                                                    record.uom ||
                                                    ""}
                                                </div>
                                              </div>
                                            ),
                                          },
                                          {
                                            title: "On Hold Qty",
                                            dataIndex: "totalOnHoldQty",
                                            key: "totalOnHoldQty",
                                            width: 120,
                                            align: "right",
                                            render: (
                                              qty: number,
                                              record: any
                                            ) => (
                                              <div>
                                                <div className="fw-bold text-warning">
                                                  {qty?.toLocaleString("en-IN") || 0}
                                                </div>
                                                <div className="text-muted small">
                                                  {record.uomName ||
                                                    record.uom ||
                                                    ""}
                                                </div>
                                              </div>
                                            ),
                                          },
                                          {
                                            title: "Amount",
                                            dataIndex: "amount",
                                            key: "amount",
                                            width: 120,
                                            render: (val: number) => (
                                              <span style={{ fontWeight: 500 }}>
                                                ₹
                                                {val?.toLocaleString("en-IN", {
                                                  maximumFractionDigits: 2,
                                                })}
                                              </span>
                                            ),
                                          },
                                        ]}
                                        pagination={false}
                                        size="small"
                                        bordered
                                        summary={() => (
                                          <Table.Summary fixed>
                                            <Table.Summary.Row
                                              style={{
                                                backgroundColor: "#e6f7ff",
                                              }}
                                            >
                                              <Table.Summary.Cell index={0}>
                                                <strong>Total</strong>
                                              </Table.Summary.Cell>
                                              <Table.Summary.Cell index={1}>
                                                <Tag
                                                  color={
                                                    totalPercentage === 100
                                                      ? "green"
                                                      : "orange"
                                                  }
                                                >
                                                  {totalPercentage}%
                                                </Tag>
                                              </Table.Summary.Cell>
                                              <Table.Summary.Cell index={2}>
                                                <strong>
                                                  ₹
                                                  {totalRateAmount.toLocaleString(
                                                    "en-IN",
                                                    { maximumFractionDigits: 2 }
                                                  )}
                                                </strong>
                                              </Table.Summary.Cell>
                                              <Table.Summary.Cell index={3}>
                                                <strong>
                                                  {totalAllocatedQty.toLocaleString("en-IN")}{" "}
                                                  {record.uomName || ""}
                                                </strong>
                                              </Table.Summary.Cell>
                                              <Table.Summary.Cell index={4}>
                                                <strong>
                                                  {totalExecutedQty.toLocaleString("en-IN")}{" "}
                                                  {record.uomName || ""}
                                                </strong>
                                              </Table.Summary.Cell>
                                              <Table.Summary.Cell index={5}>
                                                <strong className="text-primary">
                                                  {totalApprovalExecutedQty.toLocaleString("en-IN")}{" "}
                                                  {record.uomName || ""}
                                                </strong>
                                              </Table.Summary.Cell>
                                              <Table.Summary.Cell index={6}>
                                                <strong className="text-success">
                                                  {totalBilledQty.toLocaleString("en-IN")}{" "}
                                                  {record.uomName || ""}
                                                </strong>
                                              </Table.Summary.Cell>
                                              <Table.Summary.Cell index={7}>
                                                <strong className="text-warning">
                                                  {totalOnHoldQty.toLocaleString("en-IN")}{" "}
                                                  {record.uomName || ""}
                                                </strong>
                                              </Table.Summary.Cell>
                                              <Table.Summary.Cell index={8}>
                                                <strong>
                                                  ₹
                                                  {totalAmount.toLocaleString(
                                                    "en-IN",
                                                    { maximumFractionDigits: 2 }
                                                  )}
                                                </strong>
                                              </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                          </Table.Summary>
                                        )}
                                      />
                                    </div>
                                  );
                                },
                                expandIcon: ({
                                  expanded,
                                  onExpand,
                                  record,
                                }: any) => {
                                  const hasStages =
                                    record.stages && record.stages.length > 0;
                                  if (!hasStages) return null;

                                  return expanded ? (
                                    <DownOutlined
                                      onClick={(e) => onExpand(record, e)}
                                      style={{
                                        color: "#722ed1",
                                        cursor: "pointer",
                                      }}
                                    />
                                  ) : (
                                    <RightOutlined
                                      onClick={(e) => onExpand(record, e)}
                                      style={{
                                        color: "#722ed1",
                                        cursor: "pointer",
                                      }}
                                    />
                                  );
                                },
                                rowExpandable: (record: any) =>
                                  !!(record.stages && record.stages.length > 0),
                              }}
                            />
                          )}
                        </div>

                        {workOrder.workOrderLines?.length > 0 && (
                          <div className="mt-3 p-3 bg-light rounded">
                            <Row>
                              <Col lg="8">
                                <div className="d-flex justify-content-between">
                                  <span>Subtotal:</span>
                                  <strong>₹{subtotal.toLocaleString("en-IN")}</strong>
                                </div>

                                {/* GST Slab-wise Breakdown */}
                                {workOrder.enableTax &&
                                  Object.keys(gstSlabBreakdown).length > 0 && (
                                    <>
                                      <hr
                                        className="my-2"
                                        style={{
                                          margin: "8px 0",
                                          opacity: 0.3,
                                        }}
                                      />
                                      <div style={{ paddingLeft: "12px" }}>
                                        {Object.entries(gstSlabBreakdown).map(
                                          ([rate, amounts]) => (
                                            <div key={rate}>
                                              <div
                                                className="d-flex justify-content-between"
                                                style={{
                                                  fontSize: "13px",
                                                  color: "#666",
                                                }}
                                              >
                                                <span>GST @ {rate}:</span>
                                                <span></span>
                                              </div>
                                              {amounts.igstRate > 0 ? (
                                                <div
                                                  className="d-flex justify-content-between"
                                                  style={{
                                                    fontSize: "12px",
                                                    paddingLeft: "16px",
                                                  }}
                                                >
                                                  <span>IGST:</span>
                                                  <span>
                                                    ₹
                                                    {amounts.igstRate.toLocaleString("en-IN")}
                                                  </span>
                                                </div>
                                              ) : (
                                                <>
                                                  <div
                                                    className="d-flex justify-content-between"
                                                    style={{
                                                      fontSize: "12px",
                                                      paddingLeft: "16px",
                                                    }}
                                                  >
                                                    <span>CGST:</span>
                                                    <span>
                                                      ₹
                                                      {amounts.cgstRate.toLocaleString("en-IN")}
                                                    </span>
                                                  </div>
                                                  <div
                                                    className="d-flex justify-content-between"
                                                    style={{
                                                      fontSize: "12px",
                                                      paddingLeft: "16px",
                                                    }}
                                                  >
                                                    <span>SGST:</span>
                                                    <span>
                                                      ₹
                                                      {amounts.sgstRate.toLocaleString("en-IN")}
                                                    </span>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          )
                                        )}
                                      </div>
                                      <hr
                                        className="my-2"
                                        style={{
                                          margin: "8px 0",
                                          opacity: 0.3,
                                        }}
                                      />
                                    </>
                                  )}

                                {/* {workOrder.enableTax && tcsAmount > 0 && (
                                <div className="d-flex justify-content-between">
                                  <span>
                                    TCS Amount: ({workOrder.tcsTaxName})
                                  </span>
                                  <strong className="text-success">
                                    ₹{tcsAmount.toLocaleString("en-IN")}
                                  </strong>
                                </div>
                              )}
                              <hr className="my-2" /> */}
                                <div className="d-flex justify-content-between">
                                  <span>
                                    <strong>Gross Total:</strong>
                                  </span>
                                  <strong className="text-success">
                                    ₹{grossTotal.toLocaleString("en-IN")}
                                  </strong>
                                </div>
                                {/* {workOrder.enableTax && tdsAmount > 0 && (
                                <div className="d-flex justify-content-between">
                                  <span>
                                    TDS Deduction: ({workOrder.tdsTaxName})
                                  </span>
                                  <strong className="text-danger">
                                    -₹{tdsAmount.toLocaleString("en-IN")}
                                  </strong>
                                </div>
                              )} */}
                                {/* {paymentDeductions > 0 && (
                                <div className="d-flex justify-content-between">
                                  <span>Payment Deductions:</span>
                                  <strong className="text-danger">
                                    -₹{paymentDeductions.toLocaleString("en-IN")}
                                  </strong>
                                </div>
                              )} */}
                                {/* {(tdsAmount > 0 || paymentDeductions > 0) && (
                                <>
                                  <hr className="my-2" />
                                  <div className="d-flex justify-content-between">
                                    <span>
                                      <strong>Work Order Total:</strong>
                                    </span>
                                    <strong className="text-primary">
                                      ₹{netPayable.toLocaleString("en-IN")}
                                    </strong>
                                  </div>
                                </>
                              )} */}
                              </Col>
                            </Row>
                          </div>
                        )}
                      </div>
                    </Panel>

                    {/* Section C: General Conditions */}
                    <Panel
                      header={`📋 General Conditions (${workOrder.generalConditions?.length || 0
                        })`}
                      key="3"
                    >
                      <div className="mb-3">
                        <Tag color="blue">
                          {workOrder.generalConditions?.length || 0}{" "}
                          condition(s) selected
                        </Tag>
                      </div>

                      {workOrder.generalConditions &&
                        workOrder.generalConditions.length > 0 ? (
                        <div>
                          {workOrder.generalConditions
                            .sort((a, b) => a.order - b.order)
                            .map((condition: any, index) => {
                              const hasContent =
                                condition.content &&
                                condition.content.trim() !== "";
                              const hasAttachments =
                                condition.attachments &&
                                condition.attachments.length > 0;

                              return (
                                <div
                                  key={condition.id}
                                  className="mb-4 p-3 border rounded"
                                >
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h6 className="mb-0">
                                      <FileTextOutlined className="me-2" />
                                      {index + 1}. {condition.conditionCode}
                                    </h6>
                                    <div>
                                      {hasContent && (
                                        <Tag color="green">Text</Tag>
                                      )}
                                      {hasAttachments && (
                                        <Tag color="blue">Documents</Tag>
                                      )}
                                    </div>
                                  </div>

                                  {/* Text Content */}
                                  {hasContent && (
                                    <div className="mb-3">
                                      <div className="p-2 bg-light rounded">
                                        <small className="text-primary fw-medium">
                                          Content:
                                        </small>
                                        <p
                                          className="mb-0 mt-1 small"
                                          style={{ whiteSpace: "pre-wrap" }}
                                        >
                                          {condition.content}
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                  {/* Attachments */}
                                  {hasAttachments && (
                                    <div className="mt-2">
                                      <small className="text-primary fw-medium d-block mb-2">
                                        <PaperClipOutlined className="me-1" />
                                        Documents (
                                        {condition.attachments.length}
                                        ):
                                      </small>
                                      <div className="row">
                                        {condition.attachments.map(
                                          (attachment: any, idx: any) => (
                                            <div
                                              key={attachment.id}
                                              className="col-md-6 mb-2"
                                            >
                                              <div className="border rounded p-2 d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center">
                                                  <FileTextOutlined className="text-muted me-2" />
                                                  <div>
                                                    <div
                                                      className="small fw-medium"
                                                      title={
                                                        attachment.fileName
                                                      }
                                                    >
                                                      {attachment.fileName
                                                        .length > 25
                                                        ? `${attachment.fileName.substring(
                                                          0,
                                                          25
                                                        )}...`
                                                        : attachment.fileName}
                                                    </div>
                                                    <small className="text-muted">
                                                      {(
                                                        attachment.fileSize /
                                                        1024 /
                                                        1024
                                                      ).toFixed(2)}
                                                      MB
                                                    </small>
                                                  </div>
                                                </div>
                                                <div>
                                                  <small className="text-muted">
                                                    {dayjs(
                                                      attachment.uploadDate
                                                    ).format("DD/MM/YY")}
                                                  </small>
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      ) : (
                        <div className="text-center text-muted py-3">
                          No general conditions selected
                        </div>
                      )}
                    </Panel>

                    {/* Section D: Payment Conditions */}
                    <Panel header="📋 Payment Conditions" key="4">
                      <div className="mb-3">
                        <Tag color="green">
                          <DollarOutlined className="me-1" />
                          Contract Value: ₹{grossTotal.toLocaleString("en-IN")}
                        </Tag>
                      </div>

                      {workOrder?.paymentConditions &&
                        isPaymentConditionsArray(workOrder.paymentConditions) &&
                        workOrder.paymentConditions.length > 0 ? (
                        <div className="payment-conditions-container">
                          {(
                            workOrder.paymentConditions as PaymentCondition[]
                          ).map(
                            (condition: PaymentCondition, index: number) => (
                              <div
                                key={condition.id || index}
                                className="mb-4 p-3 border rounded"
                              >
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <h6 className="mb-0 text-primary">
                                    <DollarOutlined className="me-2" />
                                    {condition.conditionType}
                                  </h6>
                                  <div>
                                    <Tag
                                      color={
                                        condition.isPercent ? "blue" : "green"
                                      }
                                    >
                                      {condition.isPercent
                                        ? "Percentage"
                                        : "Fixed Amount"}
                                    </Tag>
                                    {condition.interestCharged && (
                                      <Tag color="orange">Interest Charged</Tag>
                                    )}
                                  </div>
                                </div>

                                <Descriptions bordered size="small" column={2}>
                                  <Descriptions.Item label="Amount" span={2}>
                                    {condition.isPercent ? (
                                      <div>
                                        <strong>
                                          {condition.percentValue}%
                                        </strong>{" "}
                                        of RABill
                                        <br />
                                        <small className="text-success">
                                          Calculated: ₹
                                          {condition.calculatedAmount?.toLocaleString("en-IN") ||
                                            "0"}
                                        </small>
                                      </div>
                                    ) : (
                                      <div>
                                        <strong>
                                          ₹
                                          {condition.amountValue?.toLocaleString("en-IN") ||
                                            "0"}
                                        </strong>
                                        <br />
                                        <small className="text-success">
                                          Fixed Amount: ₹
                                          {condition.calculatedAmount?.toLocaleString("en-IN") ||
                                            condition.amountValue?.toLocaleString("en-IN") ||
                                            "0"}
                                        </small>
                                      </div>
                                    )}
                                  </Descriptions.Item>

                                  {condition.interestCharged &&
                                    condition.interestRate > 0 && (
                                      <Descriptions.Item label="Interest Rate">
                                        <Tag color="orange">
                                          {condition.interestRate}% per annum
                                        </Tag>
                                      </Descriptions.Item>
                                    )}

                                  <Descriptions.Item label="Recovery Type">
                                    <Tag color="purple">
                                      {condition.recoveryType}
                                    </Tag>
                                  </Descriptions.Item>

                                  {condition.conditionType === "Mobilization" &&
                                    condition.recoveryPercentRate &&
                                    condition.recoveryPercentRate > 0 && (
                                      <Descriptions.Item label="Recovery %">
                                        <Tag color="blue">
                                          {condition.recoveryPercentRate}% per
                                          RA Bill
                                          {condition.recoveryCapPercent &&
                                            condition.recoveryCapPercent > 0 &&
                                            ` upto ${condition.recoveryCapPercent}% of Work Order`}
                                        </Tag>
                                      </Descriptions.Item>
                                    )}

                                  {condition.releaseDate && (
                                    <Descriptions.Item
                                      label="Release Date"
                                      span={2}
                                    >
                                      <div>
                                        <strong>
                                          {dayjs(condition.releaseDate).format(
                                            "DD MMM YYYY"
                                          )}
                                        </strong>
                                        <br />
                                        <small className="text-muted">
                                          {dayjs(
                                            condition.releaseDate
                                          ).fromNow()}
                                        </small>
                                      </div>
                                    </Descriptions.Item>
                                  )}

                                  {/* {condition.notes && (
                                <Descriptions.Item label="Notes" span={2}>
                                  <div className="p-2 bg-light rounded">
                                    <small className="text-muted">{condition.notes}</small>
                                  </div>
                                </Descriptions.Item>
                              )} */}
                                </Descriptions>

                                <div className="mt-2 text-end">
                                  <small className="text-muted">
                                    Created:{" "}
                                    {dayjs(condition.createdAt).format(
                                      "DD-MMM-YYYY HH:mm"
                                    )}
                                    {condition.updatedAt !==
                                      condition.createdAt && (
                                        <>
                                          {" "}
                                          | Updated:{" "}
                                          {dayjs(condition.updatedAt).format(
                                            "DD-MMM-YYYY HH:mm"
                                          )}
                                        </>
                                      )}
                                  </small>
                                </div>
                              </div>
                            )
                          )}

                          {/* Summary Card */}
                          {/* <div className="mt-4 p-3 bg-light rounded">
                          <h6 className="mb-3">Payment Conditions Summary</h6>
                          <div className="row">
                            {(
                              workOrder.paymentConditions as PaymentCondition[]
                            ).map((condition: PaymentCondition) => (
                              <div key={condition.id} className="col-md-4 mb-2">
                                <div className="text-center">
                                  <div className="fw-bold text-primary">
                                    ₹
                                    {condition.calculatedAmount?.toLocaleString("en-IN") ||
                                      "0"}
                                  </div>
                                  <small className="text-muted">
                                    {condition.conditionType}
                                  </small>
                                </div>
                              </div>
                            ))}
                          </div>
                          <hr />
                          <div className="row">
                            <div className="col-md-6">
                              <strong>Total Advance/Deductions: </strong>
                              <span className="text-success">
                                ₹
                                {(
                                  workOrder.paymentConditions as PaymentCondition[]
                                )
                                  .reduce(
                                    (
                                      sum: number,
                                      condition: PaymentCondition
                                    ) =>
                                      sum + (condition.calculatedAmount || 0),
                                    0
                                  )
                                  .toLocaleString("en-IN")}
                              </span>
                            </div>
                            <div className="col-md-6">
                              <strong>Net Payable: </strong>
                              <span className="text-primary">
                                ₹{netPayable.toLocaleString("en-IN")}
                              </span>
                              <br />
                              <small className="text-muted">
                                (Gross Total)
                              </small>
                            </div>
                          </div>
                        </div> */}
                        </div>
                      ) : workOrder?.paymentConditions &&
                        !isPaymentConditionsArray(
                          workOrder.paymentConditions
                        ) ? (
                        // Legacy object format - show message that it's not supported in this view
                        <div className="text-center text-muted py-3">
                          <div className="mb-2">
                            <Tag color="warning">Legacy Format</Tag>
                          </div>
                          <p>
                            Payment conditions are in legacy format. Please
                            contact administrator.
                          </p>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-3">
                          <Empty description="No payment conditions configured" />
                        </div>
                      )}
                    </Panel>

                    {/* Section E: Riskflags & Additional Advances */}
                    <Panel header="📋 Riskflags & Additional Advances" key="5">
                      <div className="mt-3">
                        {/* Riskflags Items - Top */}
                        <div
                          className="p-3 border rounded bg-light mb-3"
                          style={{ width: "100%" }}
                        >
                          <div className="fw-bold text-primary mb-2">
                            Riskflags
                          </div>
                          {workOrder.riskflassItems &&
                            workOrder.riskflassItems.length > 0 ? (
                            <div>
                              <Table
                                dataSource={workOrder.riskflassItems}
                                rowKey={(record, index) =>
                                  record.id?.toString() ||
                                  index?.toString() ||
                                  ""
                                }
                                pagination={false}
                                bordered
                                size="small"
                                components={{
                                  header: {
                                    cell: (props: any) => (
                                      <th
                                        {...props}
                                        style={{
                                          ...props.style,
                                          whiteSpace: "nowrap",
                                        }}
                                      />
                                    ),
                                  },
                                }}
                                columns={[
                                  {
                                    title: "S.No",
                                    key: "sno",
                                    width: 60,
                                    align: "center",
                                    render: (_: any, __: any, index: number) =>
                                      index + 1,
                                  },
                                  {
                                    title: "Name",
                                    dataIndex: "name",
                                    key: "name",
                                    width: 250,
                                    render: (name: string) => {
                                      if (!name) return "-";
                                      const maxLength = 30;
                                      const isLong = name.length > maxLength;
                                      const displayText = isLong
                                        ? name.substring(0, maxLength) + "..."
                                        : name;
                                      return (
                                        <Tooltip
                                          title={isLong ? name : null}
                                          placement="topLeft"
                                        >
                                          <div
                                            style={{
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              cursor: isLong
                                                ? "pointer"
                                                : "default",
                                            }}
                                          >
                                            {displayText}
                                          </div>
                                        </Tooltip>
                                      );
                                    },
                                  },
                                  {
                                    title: "Is Chargeable",
                                    dataIndex: "isChargable",
                                    key: "isChargable",
                                    width: 180,
                                    align: "center",
                                    render: (isChargable: boolean) => (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: "8px",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        <Checkbox
                                          checked={isChargable}
                                          disabled
                                          style={{
                                            color: "white",
                                          }}
                                          className="custom-checkbox-white"
                                        />
                                        <span>Yes</span>
                                      </div>
                                    ),
                                  },
                                  {
                                    title: "Amount",
                                    dataIndex: "totalAmount",
                                    key: "totalAmount",
                                    width: 180,
                                    align: "right",
                                    render: (amount: number) =>
                                      `₹${(amount || 0).toLocaleString(
                                        "en-IN",
                                        {
                                          maximumFractionDigits: 2,
                                        }
                                      )}`,
                                  },
                                  {
                                    title: "Cumulative Recovered Amount",
                                    key: "cumulativeRecoveredAmount",
                                    width: 200,
                                    align: "right",
                                    render: (_: any, record: any) => {
                                      const totalRecovered =
                                        record.totalRecoveredAmount || 0;
                                      const totalPending =
                                        record.totalPendingRecoveredAmount || 0;
                                      const cumulativeAmount =
                                        totalRecovered + totalPending;
                                      if (cumulativeAmount > 0) {
                                        return (
                                          <div
                                            style={{
                                              textAlign: "right",
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            <span style={{ fontWeight: 500 }}>
                                              ₹
                                              {cumulativeAmount.toLocaleString(
                                                "en-IN",
                                                {
                                                  maximumFractionDigits: 2,
                                                }
                                              )}
                                            </span>
                                          </div>
                                        );
                                      }
                                      return (
                                        <span style={{ color: "#999" }}>-</span>
                                      );
                                    },
                                  },
                                  {
                                    title: "Remarks",
                                    dataIndex: "notes",
                                    key: "notes",
                                    render: (remarks: string) => {
                                      if (!remarks) return "-";
                                      const maxLength = 50;
                                      const isLong = remarks.length > maxLength;
                                      const displayText = isLong
                                        ? remarks.substring(0, maxLength) +
                                        "..."
                                        : remarks;
                                      return (
                                        <Tooltip
                                          title={isLong ? remarks : null}
                                          placement="topLeft"
                                        >
                                          <div
                                            style={{
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              cursor: isLong
                                                ? "pointer"
                                                : "default",
                                            }}
                                          >
                                            {displayText}
                                          </div>
                                        </Tooltip>
                                      );
                                    },
                                  },
                                ]}
                              />
                              <div className="mt-3 pt-2 border-top">
                                <div className="d-flex justify-content-between">
                                  <span className="fw-bold">
                                    Total Riskflags Amount:
                                  </span>
                                  <strong className="text-warning">
                                    ₹
                                    {workOrder.riskflassItems
                                      .reduce(
                                        (sum: number, item: any) =>
                                          sum + (item.totalAmount || 0),
                                        0
                                      )
                                      .toLocaleString("en-IN", {
                                        maximumFractionDigits: 2,
                                      })}
                                  </strong>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-muted small text-center p-3">
                              No riskflags items added. Click "Add Riskflags"
                              from the actions menu to add items.
                            </div>
                          )}
                        </div>

                        {/* Advance Items - Bottom */}
                        <div
                          className="p-3 border rounded bg-light"
                          style={{ width: "100%" }}
                        >
                          <div className="fw-bold text-primary mb-2">
                            Additional Advances
                          </div>
                          {workOrder.advanceItems &&
                            workOrder.advanceItems.length > 0 ? (
                            <div>
                              <Table
                                dataSource={workOrder.advanceItems}
                                rowKey={(record, index) =>
                                  record.id?.toString() ||
                                  index?.toString() ||
                                  ""
                                }
                                pagination={false}
                                bordered
                                size="small"
                                components={{
                                  header: {
                                    cell: (props: any) => (
                                      <th
                                        {...props}
                                        style={{
                                          ...props.style,
                                          whiteSpace: "nowrap",
                                        }}
                                      />
                                    ),
                                  },
                                }}
                                columns={[
                                  {
                                    title: "S.No",
                                    key: "sno",
                                    width: 60,
                                    align: "center",
                                    render: (_: any, __: any, index: number) =>
                                      index + 1,
                                  },
                                  {
                                    title: "Name",
                                    dataIndex: "name",
                                    key: "name",
                                    width: 250,
                                    render: (name: string) => {
                                      if (!name) return "-";
                                      const maxLength = 30;
                                      const isLong = name.length > maxLength;
                                      const displayText = isLong
                                        ? name.substring(0, maxLength) + "..."
                                        : name;
                                      return (
                                        <Tooltip
                                          title={isLong ? name : null}
                                          placement="topLeft"
                                        >
                                          <div
                                            style={{
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              cursor: isLong
                                                ? "pointer"
                                                : "default",
                                            }}
                                          >
                                            {displayText}
                                          </div>
                                        </Tooltip>
                                      );
                                    },
                                  },
                                  {
                                    title: "Is Chargeable",
                                    dataIndex: "isChargable",
                                    key: "isChargable",
                                    width: 180,
                                    align: "center",
                                    render: (isChargable: boolean) => (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: "8px",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        <Checkbox
                                          checked={isChargable}
                                          disabled
                                          style={{
                                            color: "white",
                                          }}
                                          className="custom-checkbox-white"
                                        />
                                        <span>Yes</span>
                                      </div>
                                    ),
                                  },
                                  {
                                    title: "Amount",
                                    dataIndex: "totalAmount",
                                    key: "totalAmount",
                                    width: 180,
                                    align: "right",
                                    render: (amount: number) =>
                                      `₹${(amount || 0).toLocaleString(
                                        "en-IN",
                                        {
                                          maximumFractionDigits: 2,
                                        }
                                      )}`,
                                  },
                                  // {
                                  //   title: "Cumulative Recovered Amount",
                                  //   key: "cumulativeRecoveredAmount",
                                  //   width: 200,
                                  //   align: "right",
                                  //   render: (_: any, record: any) => {
                                  //     const totalRecovered =
                                  //       record.totalRecoveredAmount || 0;
                                  //     const totalPending =
                                  //       record.totalPendingRecoveredAmount || 0;
                                  //     const cumulativeAmount =
                                  //       totalRecovered + totalPending;
                                  //     if (cumulativeAmount > 0) {
                                  //       return (
                                  //         <div
                                  //           style={{
                                  //             textAlign: "right",
                                  //             whiteSpace: "nowrap",
                                  //             overflow: "hidden",
                                  //             textOverflow: "ellipsis",
                                  //           }}
                                  //         >
                                  //           <span style={{ fontWeight: 500 }}>
                                  //             ₹
                                  //             {cumulativeAmount.toLocaleString(
                                  //               "en-IN",
                                  //               {
                                  //                 maximumFractionDigits: 2,
                                  //               }
                                  //             )}
                                  //           </span>
                                  //         </div>
                                  //       );
                                  //     }
                                  //     return (
                                  //       <span style={{ color: "#999" }}>-</span>
                                  //     );
                                  //   },
                                  // },
                                  {
                                    title: "Remarks",
                                    dataIndex: "notes",
                                    key: "notes",
                                    render: (remarks: string) => {
                                      if (!remarks) return "-";
                                      const maxLength = 50;
                                      const isLong = remarks.length > maxLength;
                                      const displayText = isLong
                                        ? remarks.substring(0, maxLength) +
                                        "..."
                                        : remarks;
                                      return (
                                        <Tooltip
                                          title={isLong ? remarks : null}
                                          placement="topLeft"
                                        >
                                          <div
                                            style={{
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              cursor: isLong
                                                ? "pointer"
                                                : "default",
                                            }}
                                          >
                                            {displayText}
                                          </div>
                                        </Tooltip>
                                      );
                                    },
                                  },
                                ]}
                              />
                              <div className="mt-3 pt-2 border-top">
                                <div className="d-flex justify-content-between">
                                  <span className="fw-bold">
                                    Total Advance Amount:
                                  </span>
                                  <strong className="text-info">
                                    ₹
                                    {workOrder.advanceItems
                                      .reduce(
                                        (sum: number, item: any) =>
                                          sum + (item.totalAmount || 0),
                                        0
                                      )
                                      .toLocaleString("en-IN", {
                                        maximumFractionDigits: 2,
                                      })}
                                  </strong>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-muted small text-center p-3">
                              No advance items added. Click "Add Advance" from
                              the actions menu to add items.
                            </div>
                          )}
                        </div>
                      </div>
                    </Panel>

                    {/* Amendment specific sections */}
                    {isAmendmentView && amendmentData && (
                      <>
                        {/* Change Request Details Panel */}
                        <Panel
                          header={
                            <span>
                              Change Request Details
                              <Badge
                                color="primary"
                                style={{ marginLeft: "10px" }}
                              >
                                {amendmentData?.changeRequestComments?.length ||
                                  0}{" "}
                                Comments
                              </Badge>
                              <Badge
                                color="info"
                                style={{ marginLeft: "10px" }}
                              >
                                {amendmentData?.changeRequestFiles?.length || 0}{" "}
                                Files
                              </Badge>
                            </span>
                          }
                          key="7"
                        >
                          <Row>
                            <Col md={6}>
                              <h6>Comments</h6>
                              <Timeline>
                                {amendmentData?.changeRequestComments?.map(
                                  (comment: any, index: any) => (
                                    <Timeline.Item
                                      key={index}
                                      color={
                                        comment.commentRole === "Contractor"
                                          ? "blue"
                                          : "green"
                                      }
                                    >
                                      <div>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <strong>{comment.commentBy}</strong>
                                          <div>
                                            <Badge
                                              color={
                                                comment.commentRole ===
                                                  "Contractor"
                                                  ? "primary"
                                                  : "success"
                                              }
                                              style={{ marginRight: "10px" }}
                                            >
                                              {comment.commentRole}
                                            </Badge>
                                            <Badge color="secondary">
                                              {comment.changeType}
                                            </Badge>
                                          </div>
                                        </div>
                                        <small className="text-muted">
                                          {dayjs(comment.commentDate).format(
                                            "DD-MMM-YYYY HH:mm"
                                          )}
                                        </small>
                                        <p className="mt-2">
                                          {comment.commentText}
                                        </p>
                                      </div>
                                    </Timeline.Item>
                                  )
                                )}

                                {amendmentData?.buyerReviewComments &&
                                  amendmentData.buyerReviewComments.map(
                                    (comment: any, index: any) => (
                                      <Timeline.Item
                                        key={`buyer-${index}`}
                                        color="purple"
                                      >
                                        <div>
                                          <div className="d-flex justify-content-between align-items-center">
                                            <strong>{comment.commentBy}</strong>
                                            <div>
                                              <Badge color="warning">
                                                {comment.commentRole}
                                              </Badge>
                                              {comment.isInternal && (
                                                <Badge
                                                  color="secondary"
                                                  className="ml-2"
                                                >
                                                  Internal
                                                </Badge>
                                              )}
                                            </div>
                                          </div>
                                          <small className="text-muted">
                                            {dayjs(comment.commentDate).format(
                                              "DD-MMM-YYYY HH:mm"
                                            )}
                                          </small>
                                          <p className="mt-2">
                                            {comment.commentText}
                                          </p>
                                        </div>
                                      </Timeline.Item>
                                    )
                                  )}
                              </Timeline>
                            </Col>

                            <Col md={6}>
                              <h6>Uploaded Files</h6>
                              {amendmentData?.changeRequestFiles &&
                                amendmentData.changeRequestFiles.length > 0 ? (
                                <div>
                                  {amendmentData.changeRequestFiles.map(
                                    (file: any, index: any) => (
                                      <div
                                        key={index}
                                        className="card card-bordered mb-3"
                                      >
                                        <div className="card-inner">
                                          <div className="d-flex align-items-center">
                                            <div
                                              className="mr-3"
                                              style={{ fontSize: "24px" }}
                                            >
                                              {getFileIcon(file.fileType)}
                                            </div>
                                            <div className="flex-grow-1">
                                              <h6 className="mb-1">
                                                {file.fileName}
                                              </h6>
                                              <div className="text-muted">
                                                <small>
                                                  {formatFileSize(
                                                    file.fileSize || 0
                                                  )}{" "}
                                                  • {file.category || "General"}
                                                </small>
                                              </div>
                                              <div className="text-muted">
                                                <small>
                                                  By:{" "}
                                                  {file.uploadedBy || "Unknown"}{" "}
                                                  •{" "}
                                                  {dayjs(
                                                    file.uploadedDate ||
                                                    file.createdAt
                                                  ).format("DD-MMM-YYYY")}
                                                </small>
                                              </div>
                                              {file.description && (
                                                <div className="text-muted">
                                                  <small>
                                                    {file.description}
                                                  </small>
                                                </div>
                                              )}
                                            </div>
                                            <div className="d-flex">
                                              <Button
                                                size="sm"
                                                color="primary"
                                                outline
                                                className="mr-1"
                                                onClick={() => viewFile(file)}
                                              >
                                                <Icon name="eye" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                color="success"
                                                outline
                                                onClick={() =>
                                                  downloadFile(file)
                                                }
                                              >
                                                <Icon name="download" />
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  )}
                                </div>
                              ) : (
                                <div className="text-center text-muted py-3">
                                  No files uploaded
                                </div>
                              )}
                            </Col>
                          </Row>
                        </Panel>
                      </>
                    )}
                  </AntCollapse>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </Content>

      {/* Execution Update Modal */}
      <Modal
        title={`Work Execution Update - ${workOrder.workOrderNumber}`}
        open={executionModalVisible}
        onCancel={() => {
          setExecutionModalVisible(false);
          form.resetFields();
          setFileList([]);
        }}
        footer={null}
        width="90%"
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical">
          {/* Header Information */}
          <Row
            gutter={16}
            style={{
              marginBottom: 16,
              padding: 16,
              background: "#f5f5f5",
              borderRadius: 8,
            }}
          >
            <Col span={6}>
              <div>
                <strong>WO #:</strong> {workOrder.workOrderNumber}
              </div>
            </Col>
            <Col span={6}>
              <div>
                <strong>{isClientFlow ? "Client" : "Contractor"}:</strong>{" "}
                {workOrder.contractorName}
              </div>
            </Col>
            <Col span={6}>
              <div>
                <strong>Site:</strong> {workOrder.siteName}
              </div>
            </Col>
            <Col span={6}>
              <div>
                <strong>Total Value:</strong> ₹
                {workOrder.grossValue?.toLocaleString("en-IN") || "0"}
              </div>
            </Col>
          </Row>

          {/* Execution Details */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Form.Item
                label="Execution Period"
                name="executionPeriod"
                rules={[
                  { required: true, message: "Please select execution period" },
                ]}
              >
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item label="Notes" name="notes">
                <TextArea
                  rows={3}
                  placeholder="Enter additional notes (optional)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Form.Item label="Attachments" name="attachments">
                <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag files to upload
                  </p>
                  <p className="ant-upload-hint">
                    Support for PDF, JPG, PNG, DOCX files. Multiple files
                    allowed.
                  </p>
                </Dragger>
              </Form.Item>
            </Col>
          </Row>

          {/* Work Lines Summary */}
          {workLines.length > 0 && (
            <Row
              style={{
                marginBottom: 16,
                padding: 12,
                background: "#e6f7ff",
                borderRadius: 6,
              }}
            >
              <Col span={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <strong>Work Lines Summary:</strong>
                  <div>
                    <span>
                      Total Lines: <strong>{workLines.length}</strong>
                    </span>
                    <span style={{ marginLeft: 20 }}>
                      Total Approved Qty:{" "}
                      <strong>
                        {workLines
                          .reduce((sum, line) => sum + line.approvedQuantity, 0)
                          .toLocaleString("en-IN")}
                      </strong>
                    </span>
                    <span style={{ marginLeft: 20 }}>
                      Remaining Qty:{" "}
                      <strong>
                        {workLines
                          .reduce(
                            (sum, line) => sum + line.remainingQuantity,
                            0
                          )
                          .toLocaleString("en-IN")}
                      </strong>
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Work Lines Grid */}
          <div style={{ marginBottom: 16 }}>
            <h6 style={{ marginBottom: 16 }}>
              <strong>
                Work Lines - Execution Update ({workLines.length} lines)
              </strong>
            </h6>
            <Table
              columns={executionLinesColumns}
              dataSource={workLines}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1400, y: 400 }}
              size="small"
              bordered
            />
          </div>

          {/* Action Buttons */}
          <div
            style={{
              textAlign: "right",
              borderTop: "1px solid #f0f0f0",
              paddingTop: 16,
            }}
          >
            <Button
              onClick={() => setExecutionModalVisible(false)}
              style={{ marginRight: 8 }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              // type="primary"
              icon={<SendOutlined />}
              onClick={handleSubmitForApproval}
              loading={submitting}
              disabled={!workLines.some((line) => line.executedQuantity > 0)}
            >
              Submit for Approval
            </Button>
          </div>
        </Form>
      </Modal>

      {/* PDF Loading Modal */}
      <Modal
        open={pdfLoading}
        footer={null}
        closable={false}
        centered
        width={300}
        afterClose={() => {
          // Restore body scroll after modal fully closes
          setTimeout(() => {
            document.body.removeAttribute("style");
          }, 50);
        }}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px", fontSize: "14px", color: "#666" }}>
            Generating PDF Preview...
          </div>
        </div>
      </Modal>

      {/* Approve Signature Modal */}
      <Modal
        title="Approve Work Order"
        visible={isModalApproveVisible}
        onOk={() => {
          if (!signature.image) {
            Swal.fire({
              icon: "info",
              title: "Signature Required",
              text: "Please add your signature before approving.",
            });
            return;
          }
          openApproveConfirmSwal();
        }}
        onCancel={() => {
          setIsModalApproveVisible(false);
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
          setActiveSignatureContext(null);
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span style={{ fontSize: 14, fontWeight: 600 }}>Signature</span>

            {signature.image && (
              <button
                type="button"
                onClick={() =>
                  setSignature({ isActive: true, label: "Authorised Signatory", image: null })
                }
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                }}
              ></button>
            )}
          </div>

          {signature.image ? (
            <>
              <div
                className="border mb-3 d-flex justify-content-center align-items-center"
                style={{ padding: 16, borderRadius: 8, background: "#fff" }}
              >
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ maxWidth: 220, maxHeight: 120, objectFit: "contain" }}
                />
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  size="sm"
                  color="outline-secondary"
                  onClick={() => setSignature({ isActive: true, label: "Authorised Signatory", image: null })}
                >
                  Reset
                </Button>
              </div>
            </>
          ) : (
            <div
              style={{
                width: "100%",
                minHeight: 220,
                border: "2px dashed #dee2e6",
                borderRadius: 8,
                backgroundColor: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalApproveVisible(false)}
                  onCloseCrop={() => setIsModalApproveVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <Button color="primary" size="sm" outline style={{ minWidth: 140 }} onClick={() => setActiveSignatureContext("APPROVE")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>

                <Button
                  color="outline-primary"
                  size="sm"
                  style={{ minWidth: 140 }}
                  onClick={() => {
                    setIsModalApproveVisible(false);
                    setShowSignatureModal(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Reject Signature Modal */}
      <Modal
        title="Reject Work Order"
        visible={isModalRejectedVisible}
        onOk={() => {
          if (!pendingRejectReason || !pendingRejectReason.trim()) {
            Swal.fire({ icon: "info", title: "Reason Required", text: "Please enter a rejection reason." });
            return;
          }
          openRejectConfirmSwal();
        }}
        onCancel={() => {
          setIsModalRejectedVisible(false);
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
          setActiveSignatureContext(null);
          setPendingRejectReason("");
          form.resetFields(["reason"]);
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical" name="rejectForm">
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea rows={3} placeholder="Type your reason here..." value={pendingRejectReason} onChange={(e) => setPendingRejectReason(e.target.value)} />
          </Form.Item>

          <div className="mt-4">
            <strong style={{ fontSize: "16px" }}>Signature</strong>
          </div>

          {signature.image ? (
            <>
              <div className="signatueImgWrapper border mb-3 p-3 text-center">
                <img src={signature.image} alt="Signed" style={{ width: "180px", height: "auto" }} />
              </div>
              <div className="d-flex justify-content-end mb-3">
                <Button size="sm" color="outline-secondary" className="me-2" onClick={() => setSignature({ isActive: true, label: "Authorised Signatory", image: null })}>
                  Reset
                </Button>
              </div>

            </>
          ) : (
            <div
              style={{
                minHeight: "200px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "15px",
                border: "2px dashed #dee2e6",
                borderRadius: "8px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalRejectedVisible(false)}
                  onCloseCrop={() => setIsModalRejectedVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <Button color="primary" size="sm" outline onClick={() => setActiveSignatureContext("REJECT")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>

                <Button color="outline-primary" size="sm" onClick={() => { setIsModalRejectedVisible(false); setShowSignatureModal(true); }}>
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </Form>
      </Modal>

      {showSignatureModal && (
        <SignatureModal
          modal={showSignatureModal}
          toggle={() => {
            setShowSignatureModal(false);
            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
          onSave={(data: string) => {
            setSignature((prev: any) => ({ ...prev, image: data }));
            setShowSignatureModal(false);

            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
        />
      )}

    </>
  );
};

export default WorkOrderView;
